// Support at Home Suggestion Engine Panel – strategy comparison and optimisation for Support at Home funding.
// Renamed from SupportAtHomeSuggestionEngine for clarity (parallel to AgedCareSuggestionEnginePanel).

import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { calculateAge } from '../../utils/index.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import AnnuityPreferenceGate from '../../components/strategy/AnnuityPreferenceGate.tsx';
import { SameAsBestEstateNote } from '../../components/strategy/SameAsBestEstateNote.tsx';
import { pickCashflowCoveredLeader, cashflowCoveredNote, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, GOAL_CHIP_LABELS, friendlyGoalLabel, coveredIsBestEstate, SAME_PLAN_NOTE } from '../../strategy/cashflowCoveredGoal.ts';
import { AnnuityExplainerModal, EngineAnnuityTerms } from '../../components/strategy/AnnuityTerms.tsx';
import { EquityReleaseTerms } from '../../components/strategy/EquityReleaseTerms.tsx';
import { SAH_STRATEGY_EXPLAINERS } from './sahStrategyExplainers.ts';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { JargonTerm } from '../../components/narrative/index.tsx';
import {
    BrainCircuit, Settings, CheckCircle,
    Target, Lightbulb, AlertCircle,
    Eye, X, HelpCircle, ChevronDown, ChevronUp,
    Save, Home, Info, ShieldCheck, RefreshCw, ClipboardList
} from 'lucide-react';
import { createPortal } from 'react-dom';

interface TooltipRow {
    label?: string;
    value?: number | string;
    [key: string]: unknown;
}

interface CalculationTooltipProps {
    title: React.ReactNode;
    data?: TooltipRow[];
    children: React.ReactNode;
}

// Calculation Tooltip Component (matching Aged Care)
const CalculationTooltip = ({ title, data, children }: CalculationTooltipProps) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const tooltipHeight = tooltipRef.current.offsetHeight || 180;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let newPosition: 'top' | 'bottom' = position;
            if (spaceBelow < tooltipHeight + 16 && spaceAbove > tooltipHeight + 16) {
                newPosition = 'top';
            } else {
                newPosition = 'bottom';
            }
            setPosition(newPosition);
            setCoords({
                top: newPosition === 'top' ? rect.top - tooltipHeight - 16 : rect.bottom + 16,
                left: rect.left + rect.width / 2
            });
        }
    }, [show, position]);

    if (!Array.isArray(data)) return <>{children}</>;

    return (
        <div
            className="relative"
            ref={wrapperRef}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: 'translateX(-50%)',
                            zIndex: 10001,
                            width: '340px',
                            maxWidth: '90vw',
                            pointerEvents: 'auto'
                        }}
                        className="p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
                    >
                        <h4 className="font-bold text-sm mb-2 text-blue-300">{title}</h4>
                        <div className="space-y-1">
                            {data.map((item, index) => {
                                let displayValue: string | number | undefined = item.value;
                                if (typeof displayValue === 'number' && !isNaN(displayValue)) {
                                    displayValue = Math.round(displayValue).toLocaleString();
                                }
                                return item.label?.startsWith('---') ? (
                                    <hr key={`hr-${item.label}-${index}`} className="border-gray-600 my-1" />
                                ) : (
                                    <div key={`row-${item.label}-${index}`} className="flex justify-between">
                                        <span className="text-gray-400">{item.label}:</span>
                                        <span className="font-mono font-semibold">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

import { getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { calculateServicePlanCosts as calculateServiceCostsUtil } from '../../../ServicePlanUtils.ts';
import { analyzeBaselineOpportunities, identifyIrrelevantStrategies } from '../../strategy/SupportAtHomeProblemIdentifier.ts';
import { generateAllStrategies } from '../../strategy/SupportAtHomeStrategyGenerator.ts';
import { rankStrategies } from '../../strategy/SupportAtHomeStrategyEvaluator.ts';
import { applyRadFundingToFactFind, extractAgedCareIntegrationDetails } from '../../strategy/StrategyIntegrationUtils.ts';
import {
    buildSupportAtHomeAssumptions,
    flattenSupportAtHomeAssumptionsForModal,
} from '../../strategy/supportAtHomeStrategyAssumptions.ts';
import { resolveServicePlanForSuggestionEngine } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import {
    buildRecommendedOptimalStrategies,
    enrichStrategiesWithRecommendationLabels,
    goalLeaderFor,
} from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { listClientLiabilities } from '../../strategy/supportAtHomeStrategyFunding.ts';
import SupportAtHomeSuggestionEngineDeepDive from './SupportAtHomeSuggestionEngineDeepDive.tsx';
import type { SupportAtHomeSuggestionEngineDeepDiveProps } from './SupportAtHomeSuggestionEngineDeepDive.tsx';
import {
    buildHomeCareStrategyFromSuggestionEngine,
    persistHomeCareStrategy,
    getSavedHomeCareStrategies,
} from './homeCareStrategyStorage.tsx';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import {
    buildSupportAtHomeDigitalSOASnapshot,
    openSupportAtHomeDigitalSOAAttachment,
} from './SupportAtHomeDigitalSOAAttachment.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { hasAnsweredNewMoneyProfile, INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import SuggestionEngineAnalysisLoader, {
    SUGGESTION_ENGINE_MIN_ANALYSIS_DISPLAY_MS,
} from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import { CurrencyInput } from './SupportAtHomeHelpers.tsx';
import { StructureFundingInSuggestionsFields } from '../agedCare/StructureFundingInSuggestionsFields.tsx';
import type { StructureFundingInSuggestions } from '../agedCare/StructureFundingInSuggestionsFields.tsx';
import { Button } from '../../components/ui/index.tsx';
import { ShieldAlert } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromSupportAtHomeType } from '../../strategy/strategyRisks.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindGoals, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy, AgedCareIntegrationDetails } from '../../strategy/StrategyIntegrationUtils.ts';
import type { ProblemStatement, FilteredStrategiesResult } from '../../strategy/SupportAtHomeProblemIdentifier.ts';
import type { GoalPriority, BaselineAnalysis } from '../../strategy/SupportAtHomeStrategyEvaluator.ts';
import type {
    EvaluatedStrategy,
    StrategySnapshot,
    RecommendedStrategyEntry,
} from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import type {
    SupportAtHomeStrategySettings,
    SupportAtHomeAssumptions,
} from '../../strategy/supportAtHomeStrategyAssumptions.ts';
import type { DownsizePropertySettings } from '../../strategy/supportAtHomeDownsizeScenarioUtils.ts';

/** This panel always initialises `propertyStrategies` (unlike SupportAtHomeStrategySettings's optional
 * field, which some other callers omit), so the settings-modal JSX below can read it without `?.`. */
type SahStrategySettings = SupportAtHomeStrategySettings & { propertyStrategies: DownsizePropertySettings };
import type { SupportAtHomeServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import type { SupportAtHomeContributions, SupportAtHomeProjectionYear } from '../../strategy/supportAtHomeProjectionUtils.ts';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';

type DraftFactFindGoals = FactFindGoals;

/** Baseline financial position the suggestion engine measures every candidate strategy against. */
interface SahAnalysisBaseline {
    financials: Record<string, unknown>;
    contributions: SupportAtHomeContributions;
    projections: SupportAtHomeProjectionYear[];
    servicePlanCosts: { isEstimated: boolean; estimatedDescription: string | null | undefined };
    agedCareIntegrationApplied: boolean;
    /** The exact plan the analysis modelled — drives the snapshot's plan view and staleness check. */
    servicePlanUsed?: SupportAtHomeServicePlan | null;
    servicePlanIsEstimated?: boolean;
    servicePlanDescription?: string | null;
}

/** supportAtHomeRecommendedStrategies.ts's StrategySnapshot is a deliberately minimal shape shared
 * across modules; the server's evaluateSupportAtHomeStrategies response (rendered in the strategy
 * cards below) carries considerably more detail, so this extends it with the extra fields this
 * panel's UI actually reads. */
interface SahDisplayStrategySnapshot extends StrategySnapshot {
    baselineContributionRates?: { clinical?: number; independence?: number; everydayLiving?: number };
    agePensionBreakdown?: TooltipRow[];
    totalSupportAtHomeCosts?: number;
    grossServicePlanCosts?: number;
    supportAtHomeCostsBreakdown?: {
        grossAnnual?: number;
        clinical?: number;
        independence?: number;
        everydayLiving?: number;
        independenceContribution?: number;
        everydayContribution?: number;
        [key: string]: unknown;
    };
    taxBreakdown?: TooltipRow[];
    taxPayable?: number;
    abpIncome?: number;
    rentalIncome?: number;
    otherInvestmentIncome?: number;
    livingExpenses?: number;
}

/** calculateServicePlanCosts (ServicePlanUtils.ts) result, extended with the estimated-plan display
 * fields this panel adds before storing it in state. */
interface SahServicePlanCosts {
    totalAnnual?: number;
    clinical?: number;
    independence?: number;
    everyday?: number;
    hasServicePlan?: boolean;
    contribution?: { clinical?: number; independence?: number; everyday?: number; total?: number };
    hasUserServicePlan?: boolean;
    isEstimated?: boolean;
    estimatedDescription?: string | null;
    [key: string]: unknown;
}

interface SahAssetAllocationRow {
    description?: string;
    amount?: number | string;
    status?: 'sold' | 'partiallySold' | 'new' | 'upgraded' | string;
    assetName?: string;
    liquidatedAmount?: number;
    remainingAmount?: number;
    [key: string]: unknown;
}

/** A candidate strategy as evaluated by the server and rendered by this panel — extends the shared
 * EvaluatedStrategy with the additional snapshot/config/assetAllocation detail this UI reads. */
interface SahDisplayStrategy extends EvaluatedStrategy {
    snapshot?: SahDisplayStrategySnapshot;
    config?: { type?: string; description?: string; [key: string]: unknown };
    assetAllocation?: SahAssetAllocationRow[];
    recommendationLabels?: string[];
}

/** Result of a completed runAnalysis() pass — stored in `analysis` state and rendered throughout this panel. */
interface SahAnalysisPayload {
    strategies: SahDisplayStrategy[];
    recommendedOptimalStrategies: RecommendedStrategyEntry[];
    allEvaluatedStrategies: SahDisplayStrategy[];
    doNothingStrategy: SahDisplayStrategy;
    baseline: SahAnalysisBaseline;
    problemStatement: ProblemStatement;
    strategyFiltering: FilteredStrategiesResult;
    modellingAssumptions: SupportAtHomeAssumptions;
    totalGenerated: number;
    totalEvaluated: number;
    /** JSON of the effective service plan at run time — a mismatch with the current plan means the
     * results model services the user has since changed. */
    servicePlanFingerprint?: string;
}

/** Context passed down from StrategiesSection so a saved suggestion-engine strategy can be evaluated
 * with the same means-test settings as the rest of the page. */
interface HomeCareSaveContext {
    participantType: string;
    nwopCategory: string | null;
    meansDisclosed: boolean;
    previousITCF: number | string;
    isPartnered: boolean;
}

export interface SupportAtHomeSuggestionEnginePanelProps {
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
    /**
     * When Support at Home services begin (`YYYY-MM-DD`), blank = already receiving them. A fact
     * about the CASE, owned by StrategiesSection and passed down so the baseline and every candidate
     * are evaluated on the same timing — see `contributionContextOptions`.
     */
    supportStartDate?: string;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    servicePlan?: SupportAtHomeServicePlan;
    goals?: DraftFactFindGoals;
    onStrategySelect?: (strategy: Record<string, unknown>) => void;
    selectedStrategy?: Record<string, unknown> | null;
    legacyYears?: number;
    onNavigateToDashboard?: () => void;
    autoOpenOnMount?: boolean;
    onGoToServiceBuilder?: () => void;
    selectedAgedCareIntegration?: AgedCareIntegrationDetails | null;
    showBaseline?: boolean;
    setShowBaseline?: React.Dispatch<React.SetStateAction<boolean>>;
    homeCareSaveContext?: HomeCareSaveContext | null;
    onStrategySaved?: (saved: SavedStrategy) => void;
    onAnalysisStateChange?: (loading: boolean) => void;
}

/** One person's side of a service plan as display rows: name, quantity, indicative annual cost. */
function servicePlanSideRows(side: Record<string, { quantity?: number; frequency?: string } | undefined> | undefined) {
    const rows: Array<{ id: string; name: string; detail: string; annual: number }> = [];
    if (!side) return rows;
    const catalogue = Object.values(SUPPORT_AT_HOME_SERVICES).flatMap((c) => c.services.map((s) => ({ ...s, category: c.label })));
    Object.entries(side).forEach(([serviceId, allocation]) => {
        const quantity = Number(allocation?.quantity) || 0;
        if (quantity <= 0) return;
        const frequency = allocation?.frequency || 'monthly';
        const entry = catalogue.find((s) => s.id === serviceId);
        // Same month-count convention as the cost engine: daily ×30, weekly ×4, monthly ×1.
        const perMonth = frequency === 'daily' ? quantity * 30 : frequency === 'weekly' ? quantity * 4 : quantity;
        const annual = (entry?.medianPrice || 0) * perMonth * 12;
        rows.push({
            id: serviceId,
            name: entry?.name || serviceId,
            detail: `${quantity}/${frequency === 'daily' ? 'day' : frequency === 'weekly' ? 'week' : 'month'} · ${entry?.category || ''}`,
            annual,
        });
    });
    return rows.sort((a, b) => b.annual - a.annual);
}

/** Collapsed-by-default view of the service plan an analysis modelled — the services, how often,
 * and their indicative gross cost — so the numbers on the cards are never built on an invisible
 * plan. Renders the illustrative-package explanation when no real plan was in force. */
function ServicePlanSummaryCollapsible({ plan, isEstimated, description, isPartnered, clientName, partnerName }: {
    plan: SupportAtHomeServicePlan | null;
    isEstimated: boolean;
    description: string | null;
    isPartnered: boolean;
    clientName: string;
    partnerName: string;
}) {
    const [open, setOpen] = useState(false);
    const clientRows = servicePlanSideRows(plan?.client);
    const partnerRows = isPartnered ? servicePlanSideRows(plan?.partner) : [];
    const total = [...clientRows, ...partnerRows].reduce((sum, r) => sum + r.annual, 0);
    const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

    const renderSide = (label: string, rows: ReturnType<typeof servicePlanSideRows>) => (
        rows.length > 0 && (
            <div className="mb-3">
                {isPartnered && <p className="text-xs font-semibold text-gray-700 mb-1">{label}</p>}
                <ul className="space-y-1">
                    {rows.map((r) => (
                        <li key={r.id} className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-gray-800">{r.name} <span className="text-gray-500 text-xs">({r.detail})</span></span>
                            <span className="font-medium text-gray-900 whitespace-nowrap">{fmt(r.annual)}/yr</span>
                        </li>
                    ))}
                </ul>
            </div>
        )
    );

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
            >
                <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">
                        View service plan used in this analysis
                        <span className="ml-2 font-normal text-gray-500">
                            {isEstimated ? 'Illustrative package' : `Your plan · ${fmt(total)}/yr gross`}
                        </span>
                    </span>
                </div>
                {open ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {isEstimated && description && (
                        <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-lg px-3 py-2 mb-3">{description}</p>
                    )}
                    {renderSide(clientName, clientRows)}
                    {renderSide(partnerName, partnerRows)}
                    {clientRows.length === 0 && partnerRows.length === 0 && (
                        <p className="text-sm text-gray-600">No services recorded in this plan.</p>
                    )}
                    {(clientRows.length > 0 || partnerRows.length > 0) && (
                        <p className="text-xs text-gray-500 mt-2">
                            Gross catalogue costs at indicative median prices — your out-of-pocket contribution
                            depends on your contribution rates, shown in the panel on the right.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

const SupportAtHomeSuggestionEnginePanel = ({
    onSoaGenerated,
    supportStartDate = '',
    factFind,
    setFactFind,
    servicePlan = { client: {}, partner: {} }, // Service plan from Service Builder
    goals = {},
    onStrategySelect,
    selectedStrategy,
    legacyYears = 5,
    onNavigateToDashboard,
    autoOpenOnMount = false, // If true, open Strategy Analysis Setup when component loads
    onGoToServiceBuilder, // Optional: navigate straight to Service Builder
    selectedAgedCareIntegration = null, // Aged Care integration strategy (if selected)
    showBaseline = false,
    setShowBaseline,
    homeCareSaveContext = null,
    onStrategySaved,
    onAnalysisStateChange,
}: SupportAtHomeSuggestionEnginePanelProps) => {
    const [analysis, setAnalysis] = useState<SahAnalysisPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [isReoptimising, setIsReoptimising] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        onAnalysisStateChange?.(loading);
    }, [loading, onAnalysisStateChange]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showGoalPrioritization, setShowGoalPrioritization] = useState(false);
    const [goalPriority, setGoalPriority] = useState<GoalPriority>(null); // 'cashflow', 'estate', 'pension', or null
    const [deepDiveModal, setDeepDiveModal] = useState(false); // Deep dive modal state
    // "What could go wrong" for one card (StrategyRisksPanel).
    const [riskStrategy, setRiskStrategy] = useState<SahDisplayStrategy | null>(null);
    const [selectedStrategyForDeepDive, setSelectedStrategyForDeepDive] = useState<SahDisplayStrategy | null>(null); // Strategy for deep dive
    const [strategySettings, setStrategySettings] = useState<SahStrategySettings>({
        includeGifting: true,
        includeFuneralBonds: true,
        includeAnnuities: true,
        includePropertyStrategies: true,
        includeDebtRepayment: true,
        propertyStrategies: {
            downsize: true,
            downsizeNewHomeValue: 0,
            downsizeSellingCosts: 0,
            increaseHomeEquity: true,
            reverseMortgage: true,
            heas: true
        }
    });
    const [currentSettings] = useState({
        legacyYears: legacyYears || 5,
        emergencyFund: 50000
    });
    const [showAnnuityExplainer, setShowAnnuityExplainer] = useState(false);
    const [expandedExplanations, setExpandedExplanations] = useState<Record<string, boolean>>({
        gifting: false,
        funeralBonds: false,
        annuities: false,
        propertyStrategies: false
    });
    const [showModellingApproach, setShowModellingApproach] = useState(false); // Collapsed by default
    const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);
    const [showAllStrategiesTable, setShowAllStrategiesTable] = useState(false);
    const [showGoalLeadersModal, setShowGoalLeadersModal] = useState(false);
    const [allStrategiesSortBy, setAllStrategiesSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
    const [showSaveStrategyModal, setShowSaveStrategyModal] = useState(false);
    const [strategyPendingSave, setStrategyPendingSave] = useState<SahDisplayStrategy | null>(null);
    const [lastSavedStrategy, setLastSavedStrategy] = useState<SavedStrategy | null>(null);
    const [saveStrategyName, setSaveStrategyName] = useState('');
    const [saveStrategyNameError, setSaveStrategyNameError] = useState('');
    const [saveStrategySuccess, setSaveStrategySuccess] = useState(false);
    const [optimisationFocus, setOptimisationFocus] = useState<'covered' | 'legacy' | 'cashflow' | 'pension' | 'all'>(() => {
        if (goals?.preserveInheritance && !goals?.meetCareCosts && !goals?.maximizeGovtSupport) return 'legacy';
        if (goals?.meetCareCosts && !goals?.preserveInheritance && !goals?.maximizeGovtSupport) return 'cashflow';
        if (goals?.maximizeGovtSupport && !goals?.preserveInheritance && !goals?.meetCareCosts) return 'pension';
        // Best estate leads (owner, 24 Sep 2026).
        return 'legacy';
    });
    const [availableAgedCareStrategies, setAvailableAgedCareStrategies] = useState<SavedStrategy[]>([]); // Available Aged Care strategies
    const [selectedAgedCareStrategyId, setSelectedAgedCareStrategyId] = useState<string | null>(null); // Selected Aged Care strategy ID
    const [contributionContextMode, setContributionContextMode] = useState<'current' | 'linked'>('current'); // 'current' | 'linked'
    const hasStructures = (factFind?.structures || []).length > 0;
    const [structureFundingInSuggestions, setStructureFundingInSuggestions] = useState<StructureFundingInSuggestions>(
        () => (factFind?.structureFundingInSuggestions as StructureFundingInSuggestions) || 'exclude'
    );

    useEffect(() => {
        setStructureFundingInSuggestions((factFind?.structureFundingInSuggestions as StructureFundingInSuggestions) || 'exclude');
    }, [factFind?.structureFundingInSuggestions]);

    const toggleExplanation = (strategyKey: string) => {
        setExpandedExplanations(prev => ({
            ...prev,
            [strategyKey]: !prev[strategyKey]
        }));
    };

    // Load available Aged Care strategies from sessionStorage
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const savedStrategies: SavedStrategy[] = JSON.parse(sessionStorage.getItem('careCompanionStrategies') || '[]');
                // Filter for aged care strategies
                const agedCareStrategies = savedStrategies.filter((s) => s.type === 'aged_care');
                setAvailableAgedCareStrategies(agedCareStrategies);

                // If there's a selected strategy from parent (manual integration), use it
                if (selectedAgedCareIntegration && selectedAgedCareIntegration.id) {
                    setSelectedAgedCareStrategyId(selectedAgedCareIntegration.id);
                }
            } catch (error) {
                console.error('Error loading Aged Care strategies:', error);
            }
        }
    }, [selectedAgedCareIntegration]);
    
    // Get selected Aged Care strategy details
    const selectedAgedCareStrategyForIntegration = useMemo(() => {
        if (!selectedAgedCareStrategyId) return null;
        const selectedId = String(selectedAgedCareStrategyId);
        return availableAgedCareStrategies.find((s) => String(s.id) === selectedId) || null;
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);
    
    // Extract Aged Care integration details from selected strategy
    const effectiveAgedCareIntegration = useMemo(() => {
        if (selectedAgedCareStrategyForIntegration) {
            return extractAgedCareIntegrationDetails(selectedAgedCareStrategyForIntegration);
        }
        // Fall back to prop if provided
        return selectedAgedCareIntegration;
    }, [selectedAgedCareStrategyForIntegration, selectedAgedCareIntegration]);
    const isPartnered = factFind?.adviceFor === 'couple';
    const clientLiabilities = useMemo(() => listClientLiabilities(factFind), [factFind]);
    const clientHasDebts = clientLiabilities.length > 0;
    const shouldOfferContributionContextSwitch = isPartnered && Boolean(factFind?.goals?.aged_care || effectiveAgedCareIntegration);
    const linkedResidencyOverrides = useMemo(() => {
        if (!effectiveAgedCareIntegration) return {};
        const fromIntegration = effectiveAgedCareIntegration?.strategyOptions || {};
        const overrides = {
            ...(fromIntegration?.clientResidencyOverride ? { clientResidencyOverride: fromIntegration.clientResidencyOverride } : {}),
            ...(fromIntegration?.partnerResidencyOverride ? { partnerResidencyOverride: fromIntegration.partnerResidencyOverride } : {}),
        };
        if (Object.keys(overrides).length > 0) return overrides;
        return getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind);
    }, [effectiveAgedCareIntegration, factFind]);
    const contributionContextOptions = useMemo(() => {
        // The support start date rides the SAME options bag as the residency overrides, because the
        // server merges this bag over the baseline AND every candidate (`mergedProjectionOptions`).
        // One wiring point is what guarantees no strategy is evaluated on different timing from the
        // rest — the aged-care module's "entry timing belongs to the case" rule.
        const timing = supportStartDate ? { careStartDate: supportStartDate } : {};
        if (contributionContextMode !== 'linked') {
            return Object.keys(timing).length > 0 ? { strategyOptions: timing } : {};
        }
        return { strategyOptions: { ...linkedResidencyOverrides, ...timing } };
    }, [contributionContextMode, linkedResidencyOverrides, supportStartDate]);
    const applyLinkedAgedCareScenario = contributionContextMode === 'linked' && Boolean(effectiveAgedCareIntegration);
    const canSelectLinkedContext = Boolean(selectedAgedCareIntegration) || availableAgedCareStrategies.length > 0;
    const isLinkedContextUnavailable = contributionContextMode === 'linked' && Object.keys(linkedResidencyOverrides || {}).length === 0;

    useEffect(() => {
        if (selectedAgedCareIntegration?.id) {
            setContributionContextMode('linked');
        }
    }, [selectedAgedCareIntegration]);

    const openSaveStrategyModal = (strategy: SahDisplayStrategy) => {
        setStrategyPendingSave(strategy);
        setSaveStrategyName((strategy?.name || 'Suggested strategy').replace(/\s*\([^)]*\)\s*$/, '').trim());
        setSaveStrategyNameError('');
        setSaveStrategySuccess(false);
        setShowSaveStrategyModal(true);
        if (onStrategySelect) {
            onStrategySelect(strategy);
        }
    };

    const handleConfirmSaveStrategy = async () => {
        if (!strategyPendingSave || !homeCareSaveContext) return;
        if (!saveStrategyName.trim()) {
            setSaveStrategyNameError('Please enter a strategy name');
            return;
        }
        setSaveStrategyNameError('');
        const linkedAgedCareForSave = contributionContextMode === 'linked' && effectiveAgedCareIntegration;
        const saved = await buildHomeCareStrategyFromSuggestionEngine({
            factFind,
            evaluatedStrategy: strategyPendingSave,
            name: saveStrategyName,
            selectedAgedCareIntegration: linkedAgedCareForSave ? effectiveAgedCareIntegration : null,
            agedCareResidencyOverrides: linkedAgedCareForSave ? linkedResidencyOverrides : {},
            participantType: homeCareSaveContext.participantType,
            nwopCategory: homeCareSaveContext.nwopCategory,
            meansDisclosed: homeCareSaveContext.meansDisclosed,
            previousITCF: Number(homeCareSaveContext.previousITCF) || 0,
            isPartnered: homeCareSaveContext.isPartnered,
            agedCareContext: linkedAgedCareForSave ? 'linked' : 'current',
        });
        persistHomeCareStrategy(saved);
        onStrategySaved?.(saved);
        setLastSavedStrategy(saved);
        // No auto-close: the success state offers the Digital SOA (generated from the SAVED record,
        // so the document always matches something on file — same discipline as aged care).
        setSaveStrategySuccess(true);
    };

    const closeSaveStrategyModal = () => {
        setShowSaveStrategyModal(false);
        setStrategyPendingSave(null);
        setSaveStrategySuccess(false);
        setLastSavedStrategy(null);
    };

    /** Build and open the Digital SOA for the just-saved strategy, and persist the snapshot onto it
     * so the exact document can be reopened later. */
    const openDigitalSoaForSavedStrategy = async () => {
        if (!lastSavedStrategy) return;
        const snapshot = await buildSupportAtHomeDigitalSOASnapshot({
            selectedStrategy: lastSavedStrategy,
            factFind,
            projections: ((strategyPendingSave as { projections?: unknown } | null)?.projections ?? null) as never,
            analysis: analysis ? { strategies: analysis.strategies as never, totalEvaluated: analysis.totalEvaluated } : null,
            servicePlan: servicePlanResolution.plan,
            servicePlanIsEstimated: servicePlanResolution.isEstimated,
            servicePlanDescription: servicePlanResolution.description,
            strategySource: 'suggestion_engine',
        });
        if (snapshot) {
            const document = { ...lastSavedStrategy, soaSnapshot: snapshot };
            persistHomeCareStrategy(document as never);
            onSoaGenerated?.(document as Record<string, unknown>);
            openSupportAtHomeDigitalSOAAttachment(snapshot);
        }
        closeSaveStrategyModal();
    };

    // Auto-open Strategy Analysis Setup when Suggestion Engine tab is first selected
    useEffect(() => {
        if (autoOpenOnMount) {
            setShowSettingsModal(true);
        }
    }, [autoOpenOnMount]);

    // Check if all goals are selected
    useEffect(() => {
        const allGoalsSelected = goals.meetCareCosts && goals.preserveInheritance && goals.maximizeGovtSupport;
        if (allGoalsSelected && !goalPriority) {
            setShowGoalPrioritization(true);
        }
    }, [goals, goalPriority]);

    // Format currency helper
    const formatCurrency = (value: number | string | null | undefined): string => {
        const num = Number(value);
        if (!value || isNaN(num)) return '$0';
        const roundedValue = Math.round(num);
        return `${roundedValue < 0 ? '-' : ''}$${Math.abs(roundedValue).toLocaleString()}`;
    };

    // Which service plan the engine models. Default: the current Service Builder plan (the
    // `servicePlan` prop, read from session storage by the parent). The user can instead pick one of
    // their SAVED service plans in Strategy Analysis Setup — choosing one also becomes the app-wide
    // "current plan" so the builder, insight panel and engine all agree on what's in force.
    const [servicePlanOverride, setServicePlanOverride] = useState<SupportAtHomeServicePlan | null>(null);
    const [servicePlanOverrideId, setServicePlanOverrideId] = useState<string>('');
    const effectiveServicePlan = servicePlanOverride ?? servicePlan;

    /** Saved home-care strategies that actually carry a service plan, normalised to {client, partner}. */
    const savedServicePlans = useMemo(() => {
        const toSides = (services: Record<string, unknown>): SupportAtHomeServicePlan =>
            (services.client || services.partner)
                ? { client: (services.client || {}) as SupportAtHomeServicePlan['client'], partner: (services.partner || {}) as SupportAtHomeServicePlan['partner'] }
                : { client: services as SupportAtHomeServicePlan['client'], partner: {} };
        const hasAny = (plan: SupportAtHomeServicePlan) =>
            [plan.client, plan.partner].some((side) => Object.values(side || {}).some((d) => Number(d?.quantity) > 0));
        return getSavedHomeCareStrategies()
            .filter((s) => s?.inputs?.services)
            .map((s) => ({ id: String(s.id), name: s.name || 'Saved plan', plan: toSides(s.inputs!.services as Record<string, unknown>) }))
            .filter((entry) => hasAny(entry.plan));
    }, []);

    const selectSavedServicePlan = (id: string) => {
        setServicePlanOverrideId(id);
        const chosen = savedServicePlans.find((p) => p.id === id) || null;
        setServicePlanOverride(chosen ? chosen.plan : null);
        if (chosen && typeof window !== 'undefined') {
            try {
                sessionStorage.setItem('careCompanionCurrentServicePlan', JSON.stringify(chosen.plan));
            } catch { /* storage full/blocked — the in-memory override still drives this run */ }
        }
    };

  // Service plan for modelling (user plan or documented illustrative mix)
    const servicePlanResolution = useMemo(() => {
        const isPartnered = factFind?.adviceFor === 'couple';
        return resolveServicePlanForSuggestionEngine(effectiveServicePlan, isPartnered);
    }, [effectiveServicePlan, factFind?.adviceFor]);

    const [servicePlanCosts, setServicePlanCosts] = useState<SahServicePlanCosts>({});
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const baselineFinancials = await calculationApi.calculateFinancials(factFind, contributionContextOptions.strategyOptions || {});
            const baselineContributions = await calculationApi.calculateIndividualContributions(
                factFind,
                baselineFinancials,
                'standard',
                null,
                true,
                0,
                0.5,
                contributionContextOptions
            );
            if (cancelled) return;

            const isPartnered = factFind?.adviceFor === 'couple';
            const contributionRates = isPartnered
                ? (baselineContributions?.combined?.contributionRates || {})
                : (baselineContributions?.client?.contributionRates || {});

            const costs = calculateServiceCostsUtil(
                servicePlanResolution.plan,
                contributionRates,
                isPartnered
            );

            setServicePlanCosts({
                ...costs,
                hasUserServicePlan: !servicePlanResolution.isEstimated,
                isEstimated: servicePlanResolution.isEstimated,
                estimatedDescription: servicePlanResolution.description,
            });
        })();
        return () => { cancelled = true; };
    }, [servicePlanResolution, factFind, contributionContextOptions]);

    /**
     * The just-in-time "how would new money be invested?" gate. Downsize strategies deploy the
     * freed-up money, so when the client has a home and no recorded answer the question is asked
     * BEFORE the engine prices anything — same rules as the village module (answering saves to
     * the profile and is asked once ever; skipping runs at Conservative without recording).
     */
    const [showNewMoneyGate, setShowNewMoneyGate] = useState(false);
    const [newMoneySkipped, setNewMoneySkipped] = useState(false);
    /*
     * THE ANNUITY WILLINGNESS QUESTION (owner, 25 Sep 2026). "No" removes every annuity strategy
     * from this and every other module — the answer is the client's (annuityPreference.ts).
     */
    const [annuityPrefSkipped, setAnnuityPrefSkipped] = useState(false);
    const needsAnnuityAnswer = !annuityPreferenceOf(factFind as never).answered && !annuityPrefSkipped;
    const answerAnnuityPreference = (appetite: AnnuityAppetite, residual: AnnuityResidual) => {
        if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), annuityAppetite: appetite, annuityResidual: residual } }));
        else setAnnuityPrefSkipped(true);
    };
    const [showTbcGate, setShowTbcGate] = useState(false);
    const [tbcSkipped, setTbcSkipped] = useState(false);
    const sahRunWouldInvest = (parseFloat(String(factFind?.home?.value ?? '')) || 0) > 0;
    // Chained after the new-money question: SAH's downsize strategies can commence a pension from
    // the downsizer contribution, so the $1.6m screen asks for the ATO cap figure before pricing.
    const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && sahRunWouldInvest
        ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
            : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
        : null;
    /**
     * @param askedInSetup - the caller has just shown the settings modal, which carries the
     * new-money question itself. Its default option ("Not sure — assume Conservative") is a
     * legitimate answer that deliberately records nothing, so gating on a RECORDED answer put the
     * interim screen back in front of every adviser who left the dropdown alone — the exact screen
     * moving the question into the setup was meant to remove. The gate below stays for any future
     * caller that runs without opening the modal.
     */
    const maybeRunAnalysis = (askedInSetup = false) => {
        if (!askedInSetup && sahRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped) {
            setShowNewMoneyGate(true);
            return;
        }
        if (tbcPersonNeedingAnswer !== null) {
            setShowTbcGate(true);
            return;
        }
        runAnalysis();
    };
    const answerNewMoneyProfile = (id: InvestmentProfileId) => {
        setShowNewMoneyGate(false);
        if (setFactFind) {
            setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
        }
        if (tbcPersonNeedingAnswer !== null) { setShowTbcGate(true); return; }
        runAnalysis();
    };
    const skipNewMoneyProfile = () => {
        setShowNewMoneyGate(false);
        setNewMoneySkipped(true);
        if (tbcPersonNeedingAnswer !== null) { setShowTbcGate(true); return; }
        runAnalysis();
    };
    const answerTbc = (amount: number) => {
        setShowTbcGate(false);
        if (setFactFind && tbcPersonNeedingAnswer) {
            const key = tbcPersonNeedingAnswer;
            setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
        }
        runAnalysis();
    };
    const skipTbc = () => {
        setShowTbcGate(false);
        setTbcSkipped(true);
        runAnalysis();
    };

    // Run intelligent modelling analysis
    const runAnalysis = async () => {
        setIsReoptimising(!!analysis);
        setLoading(true);
        setError(null);
        setProgress({ current: 0, total: 0 });

        const runWork = async (): Promise<{ payload: SahAnalysisPayload; hasStrategies: boolean }> => {
            // STEP 0: Apply Aged Care integration to factFind if selected (CRITICAL: Use this as baseline)
            let baselineFactFind: DraftFactFind = structuredClone(factFind);
            if (hasStructures) {
                baselineFactFind.structureFundingInSuggestions = structureFundingInSuggestions;
            }
            if (applyLinkedAgedCareScenario) {
                // Apply RAD funding if present
                if (effectiveAgedCareIntegration?.radFunding) {
                    baselineFactFind = applyRadFundingToFactFind(baselineFactFind, effectiveAgedCareIntegration.radFunding);
                }

                // Apply living expenses adjustment if present
                if (effectiveAgedCareIntegration?.livingExpenses !== undefined && effectiveAgedCareIntegration?.livingExpenses !== null) {
                    if (!baselineFactFind.expenses) {
                        baselineFactFind.expenses = {};
                    }
                    baselineFactFind.expenses.livingExpenses = effectiveAgedCareIntegration.livingExpenses;
                }

            }
            
            // STEP 1: Analyze baseline and identify problems (using Aged Care-adjusted factFind)
            const baselineAnalysis = await analyzeBaselineOpportunities(
                baselineFactFind,
                effectiveServicePlan,
                currentSettings.legacyYears || legacyYears || 5,
                {
                    strategyOptions: contributionContextOptions.strategyOptions || {},
                    contributionOptions: contributionContextOptions
                }
            );

            // STEP 2: Identify irrelevant strategies based on baseline analysis
            const strategyFiltering = identifyIrrelevantStrategies(
                baselineAnalysis.problemStatement,
                strategySettings
            );

            // STEP 3: Generate all relevant strategies (using Aged Care-adjusted factFind)
            const allStrategies = generateAllStrategies(
                baselineFactFind,
                baselineAnalysis.problemStatement,
                strategySettings
            );

            const evaluationOptions = {
                strategyOptions: contributionContextOptions.strategyOptions || {},
                contributionOptions: contributionContextOptions
            };

            setProgress({ current: 0, total: allStrategies.length + 1 });

            // STEP 4: Evaluate "Do Nothing" + every candidate strategy in ONE request (server-side
            // composite endpoint — see server/src/rest/calculation/suggestionEngine/supportAtHome.ts)
            // instead of one request per candidate.
            const baselineData = {
                baselineFinancials: baselineAnalysis.baselineFinancials,
                baselineContributions: baselineAnalysis.baselineContributions,
                baselineProjections: baselineAnalysis.baselineProjections,
            };
            const [doNothingStrategy, ...evaluatedStrategies] = await calculationApi.evaluateSupportAtHomeStrategies(
                baselineFactFind,
                baselineData,
                effectiveServicePlan as unknown as Record<string, unknown>,
                currentSettings.legacyYears || legacyYears || 5,
                [{ type: 'doNothing', name: 'Do Nothing' }, ...allStrategies],
                evaluationOptions
            );

            setProgress({ current: allStrategies.length + 1, total: allStrategies.length + 1 });

            // STEP 5: Rank strategies by goal priority (always include Do Nothing baseline). rankStrategies'
            // own BaselineAnalysis type is a loosely-typed subset shape (see SupportAtHomeStrategyEvaluator.ts);
            // baselineAnalysis here is the richer BaselineOpportunitiesResult, which is structurally a superset.
            const rankedOthers = rankStrategies(
                evaluatedStrategies,
                goalPriority,
                baselineAnalysis as unknown as BaselineAnalysis
            );
            const recommendedOptimalStrategies = buildRecommendedOptimalStrategies(
                evaluatedStrategies,
                doNothingStrategy
            );
            const rankedStrategies = enrichStrategiesWithRecommendationLabels(
                [doNothingStrategy, ...rankedOthers],
                recommendedOptimalStrategies
            );
            
            // STEP 6: Store results
            const modellingAssumptions = buildSupportAtHomeAssumptions({
                factFind: baselineFactFind,
                strategySettings,
                legacyYears: currentSettings.legacyYears || legacyYears || 5,
                emergencyFund: currentSettings.emergencyFund || 50000,
                agedCareIntegrationApplied: applyLinkedAgedCareScenario,
                usesEstimatedServicePlan: baselineAnalysis.servicePlanResolution?.isEstimated,
                estimatedServicePlanDescription: baselineAnalysis.servicePlanResolution?.description,
            });

            return {
                payload: {
                    strategies: rankedStrategies,
                    recommendedOptimalStrategies,
                    allEvaluatedStrategies: evaluatedStrategies,
                    doNothingStrategy,
                    baseline: {
                        financials: baselineAnalysis.baselineFinancials,
                        contributions: baselineAnalysis.baselineContributions,
                        projections: baselineAnalysis.baselineProjections,
                        servicePlanCosts: baselineAnalysis.servicePlanCosts,
                        agedCareIntegrationApplied: applyLinkedAgedCareScenario,
                        // The exact plan this analysis modelled — for the snapshot's "view service
                        // plan" and for detecting that the plan changed after the run.
                        servicePlanUsed: baselineAnalysis.servicePlanResolution?.plan ?? null,
                        servicePlanIsEstimated: baselineAnalysis.servicePlanResolution?.isEstimated ?? true,
                        servicePlanDescription: baselineAnalysis.servicePlanResolution?.description ?? null,
                    },
                    servicePlanFingerprint: JSON.stringify(effectiveServicePlan ?? {}),
                    problemStatement: baselineAnalysis.problemStatement,
                    strategyFiltering,
                    modellingAssumptions,
                    totalGenerated: allStrategies.length,
                    totalEvaluated: evaluatedStrategies.length,
                },
                hasStrategies: rankedStrategies.length > 0,
            };
        };

        try {
            const [workResult] = await Promise.all([
                runWork(),
                new Promise((resolve) => setTimeout(resolve, SUGGESTION_ENGINE_MIN_ANALYSIS_DISPLAY_MS)),
            ]);

            if (workResult?.payload) {
                setAnalysis(workResult.payload);
            }

            // No intermediary "Analysis Complete" modal — the results render directly below,
            // exactly as the aged care module does after its analysis run.
            if (workResult && !workResult.hasStrategies) {
                setError('No viable strategies found for your current situation. Please adjust your strategy settings or check your financial position.');
            }
        } catch (err) {
            console.error('Error running Support at Home analysis:', err);
            setError('Failed to run analysis. Please try again.');
        } finally {
            setLoading(false);
            setIsReoptimising(false);
            setProgress({ current: 0, total: 0 });
        }
    };

    const recommendedOptimal = analysis?.recommendedOptimalStrategies || [];

    const effectiveOptimisationSurface = optimisationFocus === 'all' ? 'legacy' : optimisationFocus;

    const displayedGoalStrategies = useMemo(() => {
        if (!analysis?.strategies?.length) return [] as SahDisplayStrategy[];
        // A goal leader only exists where a strategy BEATS "Do Nothing" on that goal
        // (goalLeaderFor returns null otherwise, and the empty state below says so honestly
        // with the do-nothing figures). Best legacy and All goals share the legacy surface —
        // single primary card, AC parity.
        // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
        if (effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(recommendedOptimal)) return [] as SahDisplayStrategy[];
        const row = goalLeaderFor(recommendedOptimal, effectiveOptimisationSurface);
        if (!row) return [] as SahDisplayStrategy[];
        return [(analysis.strategies.find((s) => s.name === row.strategy.name) || row.strategy) as SahDisplayStrategy];
    }, [analysis?.strategies, recommendedOptimal, effectiveOptimisationSurface]);

    useEffect(() => {
        setShowAllStrategiesTable(false);
    }, [optimisationFocus]);

    const goalComparisonRows = useMemo(() => {
        const enrich = (label: string): SahDisplayStrategy | null => {
            const row = recommendedOptimal.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            return (analysis?.strategies?.find((s) => s.name === row.strategy.name) || row.strategy) as SahDisplayStrategy;
        };
        const rows: { goalLabel: string; strategy: SahDisplayStrategy }[] = [];
        const cov = enrich(CASHFLOW_COVERED_LABEL);
        if (cov) rows.push({ goalLabel: CASHFLOW_COVERED_CHIP, strategy: cov });
        const leg = enrich('Optimal Legacy');
        if (leg) rows.push({ goalLabel: BEST_ESTATE_CHIP, strategy: leg });
        const cf = enrich('Optimal Cashflow');
        if (cf) rows.push({ goalLabel: 'Best cashflow', strategy: cf });
        const pen = enrich('Optimal Age Pension');
        if (pen) rows.push({ goalLabel: 'Best pension', strategy: pen });
        return rows;
    }, [recommendedOptimal, analysis?.strategies]);

    const tableStrategies = useMemo(() => {
        const base = (analysis?.allEvaluatedStrategies || analysis?.strategies || []).filter(
            (s) => s.name !== 'Do Nothing' && s.config?.type !== 'doNothing'
        );
        const sorted = [...base];
        if (allStrategiesSortBy === 'cashflow') {
            sorted.sort(
                (a, b) =>
                    (b.snapshot?.netCashflow ?? b.snapshot?.cashflow ?? -Infinity) -
                    (a.snapshot?.netCashflow ?? a.snapshot?.cashflow ?? -Infinity)
            );
        } else if (allStrategiesSortBy === 'pension') {
            sorted.sort(
                (a, b) =>
                    (b.snapshot?.pension ?? b.snapshot?.agePension ?? 0) -
                    (a.snapshot?.pension ?? a.snapshot?.agePension ?? 0)
            );
        } else {
            sorted.sort(
                (a, b) => (b.snapshot?.legacyValue ?? -Infinity) - (a.snapshot?.legacyValue ?? -Infinity)
            );
        }
        return sorted;
    }, [analysis, allStrategiesSortBy]);

    const assumptionsModalBullets = useMemo(
        () => flattenSupportAtHomeAssumptionsForModal(analysis?.modellingAssumptions),
        [analysis?.modellingAssumptions]
    );

    const saSuggestionCardHighlightKey = useMemo(() => {
        const inner = (selectedStrategy?.strategy ?? selectedStrategy) as SahDisplayStrategy | null | undefined;
        if (!inner?.name || !displayedGoalStrategies.length) return null;
        const k = String(inner.name);
        return displayedGoalStrategies.some((s) => String(s.name) === k) ? k : null;
    }, [selectedStrategy, displayedGoalStrategies]);

    if (loading) {
        return (
            <SuggestionEngineAnalysisLoader
                title={isReoptimising ? 'Re-optimising with new settings...' : 'Optimising your Support at Home strategies'}
                subtitle="We're working through a large number of simulated options - this may take a few minutes."
                progress={progress}
                progressLabel="Evaluating strategy"
            />
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold mb-2">Support at Home Strategy Modeller</h2>
                        <p className="text-blue-100 text-sm">
                            Intelligent modelling of financial strategies to explore ways to reduce your Support at Home contributions
                            while considering your priorities and financial goals.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {analysis?.modellingAssumptions && (
                            <button
                                type="button"
                                onClick={() => setShowAssumptionsModal(true)}
                                className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-white/30 text-sm"
                            >
                                Assumptions
                            </button>
                        )}
                        {setShowBaseline && (
                            <button
                                type="button"
                                onClick={() => setShowBaseline(!showBaseline)}
                                className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-white/30 text-sm"
                            >
                                {showBaseline ? 'Hide Baseline' : 'Show Baseline'}
                            </button>
                        )}
                        <button
                            onClick={() => setShowSettingsModal(true)}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 backdrop-blur-sm border border-white/30"
                            title="Settings"
                        >
                            <Settings className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Goal Prioritization Modal */}
            {showGoalPrioritization && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-2xl font-bold text-gray-900">Set Your Priority</h3>
                            <button
                                onClick={() => setShowGoalPrioritization(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-6">
                            You've selected all goals. To model effectively, please tell us which is most important to you:
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => {
                                    setGoalPriority('cashflow');
                                    setShowGoalPrioritization(false);
                                }}
                                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                            >
                                <div className="font-semibold text-gray-900">Maximize Cashflow</div>
                                <div className="text-sm text-gray-600">Prioritize strategies that increase your available cashflow to meet care costs</div>
                            </button>
                            <button
                                onClick={() => {
                                    setGoalPriority('estate');
                                    setShowGoalPrioritization(false);
                                }}
                                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                            >
                                <div className="font-semibold text-gray-900">Preserve Inheritance</div>
                                <div className="text-sm text-gray-600">Prioritize strategies that maximize your estate value for beneficiaries</div>
                            </button>
                            <button
                                onClick={() => {
                                    setGoalPriority('pension');
                                    setShowGoalPrioritization(false);
                                }}
                                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                            >
                                <div className="font-semibold text-gray-900">Maximize Government Support</div>
                                <div className="text-sm text-gray-600">Prioritize strategies that maximize your Age Pension and minimize contributions</div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Strategy Analysis Setup Modal */}
            {showSettingsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
                        {/* Header with app's gradient — no overflow-hidden (clips subtitle); modal shell handles rounding */}
                        <div className="bg-gradient-to-r from-blue-500 to-teal-400 text-white px-6 pt-6 pb-7 relative shrink-0">
                            <div className="absolute inset-0 bg-black opacity-10 pointer-events-none" aria-hidden="true" />
                            <div className="relative z-10">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm shrink-0 mt-0.5">
                                            <BrainCircuit className="w-6 h-6" />
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="text-2xl font-bold mb-1.5 leading-tight">Strategy Analysis Setup</h2>
                                            <p className="text-blue-100 text-sm leading-relaxed pr-2">
                                                Let's explore financial strategies that may help reduce your Support at Home contributions
                                            </p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => setShowSettingsModal(false)}
                                        className="text-white hover:text-blue-200 text-2xl leading-none transition-colors p-1 hover:bg-white hover:bg-opacity-10 rounded-full"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto flex-1 min-h-0">
                            <div className="space-y-6 pb-4">
                                {hasStructures && (
                                    <StructureFundingInSuggestionsFields
                                        value={structureFundingInSuggestions}
                                        onChange={setStructureFundingInSuggestions}
                                    />
                                )}

                                {shouldOfferContributionContextSwitch && (
                                    <div className="p-4 border rounded-lg bg-slate-50 border-slate-200">
                                        <p className="text-sm font-semibold text-gray-900 mb-2">
                                            Which contribution setting should we use?
                                        </p>
                                        <div className="space-y-3 text-sm text-gray-700">
                                            <label className="flex items-start gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="contributionContextMode"
                                                    checked={contributionContextMode === 'current'}
                                                    onChange={() => setContributionContextMode('current')}
                                                    className="mt-1 shrink-0"
                                                />
                                                <span className="min-w-0 leading-relaxed">
                                                    <strong>Current living arrangement</strong> (default) — based on your current Client Profile living situation.
                                                </span>
                                            </label>
                                            <label className="flex items-start gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="contributionContextMode"
                                                    checked={contributionContextMode === 'linked'}
                                                    onChange={() => setContributionContextMode('linked')}
                                                    className="mt-1 shrink-0"
                                                    disabled={!canSelectLinkedContext}
                                                />
                                                <span className="min-w-0 leading-relaxed">
                                                    <strong>Linked aged care scenario</strong> — use this when your selected aged care strategy assumes one partner has moved into residential care.
                                                </span>
                                            </label>
                                        </div>
                                        {/* Only ask WHICH strategy once the linked option is actually chosen —
                                            showing the dropdown under the default option invites picking a
                                            strategy that then does nothing. */}
                                        {contributionContextMode === 'linked' && availableAgedCareStrategies.length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-slate-200">
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Linked aged care strategy
                                                </label>
                                                <select
                                                    value={selectedAgedCareStrategyId ? String(selectedAgedCareStrategyId) : ''}
                                                    onChange={(e) => setSelectedAgedCareStrategyId(e.target.value || null)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                >
                                                    <option value="">Select a strategy...</option>
                                                    {availableAgedCareStrategies.map((strategy) => (
                                                        <option key={strategy.id} value={String(strategy.id)}>
                                                            {strategy.name} • {formatCurrency(strategy.outputs?.agedCareCost || 0)} p.a.
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {availableAgedCareStrategies.length === 0 && contributionContextMode === 'linked' && (
                                            <p className="text-xs text-amber-700 mt-2 leading-relaxed">
                                                No saved aged care strategies found yet. Create one in the Aged Care module to use this option.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Service plan reminder / shortcut */}
                                <div className={`mb-4 p-4 border rounded-lg flex items-start gap-3 ${
                                    servicePlanCosts.hasUserServicePlan
                                        ? 'bg-white border-slate-200/70 border-l-4 border-blue-500'
                                        : 'bg-white border-slate-200/70 border-l-4 border-amber-400'
                                }`}>
                                    {servicePlanCosts.hasUserServicePlan ? (
                                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                                    )}
                                    <div className="text-sm text-gray-800">
                                        {servicePlanCosts.hasUserServicePlan ? (
                                            <>
                                                <p className="font-semibold mb-1">Using your saved Service Plan</p>
                                                <p className="mb-2">
                                                    We'll model strategies based on your current Support at Home service plan.
                                                    You can review or adjust your services at any time.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="font-semibold mb-1">No Service Plan Selected</p>
                                                <p className="mb-2">
                                                    You can still run strategy comparisons now. We'll use a documented illustrative service mix so Support at Home contribution costs, cashflow, and legacy are modelled consistently across strategies.
                                                    {servicePlanCosts.estimatedDescription && (
                                                        <span className="block mt-2 text-slate-600">{servicePlanCosts.estimatedDescription}</span>
                                                    )}
                                                    Build your Support at Home service plan if you want costs based on your actual assessed services.
                                                </p>
                                            </>
                                        )}
                                        {savedServicePlans.length > 0 && (
                                            <div className="mb-3">
                                                <label htmlFor="se-saved-service-plan" className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Service plan to model
                                                </label>
                                                <select
                                                    id="se-saved-service-plan"
                                                    value={servicePlanOverrideId}
                                                    onChange={(e) => selectSavedServicePlan(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                >
                                                    <option value="">
                                                        {servicePlanCosts.hasUserServicePlan
                                                            ? 'Current Service Builder plan'
                                                            : 'No plan — use the illustrative package'}
                                                    </option>
                                                    {savedServicePlans.map((p) => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </select>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Choosing a saved plan makes it the plan in force everywhere — the Service
                                                    Builder and this analysis stay in sync.
                                                </p>
                                            </div>
                                        )}
                                        {onGoToServiceBuilder && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowSettingsModal(false);
                                                    onGoToServiceBuilder();
                                                }}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700"
                                            >
                                                <Home className="w-4 h-4" />
                                                Go to Service Builder
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Optimization Approach Section - Collapsible */}
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setShowModellingApproach(!showModellingApproach)}
                                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Target className="w-5 h-5 text-blue-600" />
                                            <h3 className="text-base font-semibold text-gray-900">How we compare your options</h3>
                                        </div>
                                        {showModellingApproach ? (
                                            <ChevronUp className="w-5 h-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-gray-500" />
                                        )}
                                    </button>
                                    
                                    {showModellingApproach && (
                                        <div className="px-4 pb-4 border-t border-gray-200">
                                            <div className="pt-4 space-y-4">
                                                {/* What We'll Do */}
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 mb-2">What happens next:</p>
                                                    <ul className="space-y-2 text-sm text-gray-700">
                                                        <li className="flex items-start gap-2">
                                                            <span className="text-blue-600 font-semibold mt-0.5">1.</span>
                                                            <span>We test each selected option one at a time so you can see the effect on pension, cash flow, contribution rates, and estate value.</span>
                                                        </li>
                                                        <li className="flex items-start gap-2">
                                                            <span className="text-blue-600 font-semibold mt-0.5">2.</span>
                                                            <span>Non-viable options (those that worsen every key metric without offsetting benefit) are filtered out before ranking.</span>
                                                        </li>
                                                        <li className="flex items-start gap-2">
                                                            <span className="text-blue-600 font-semibold mt-0.5">3.</span>
                                                            <span>We rank results based on what you said matters most.</span>
                                                        </li>
                                                        <li className="flex items-start gap-2">
                                                            <span className="text-blue-600 font-semibold mt-0.5">4.</span>
                                                            <span>For each option, we show the trade-offs in plain numbers.</span>
                                                        </li>
                                                    </ul>
                                                </div>

                                                {/* Goal-Based Prioritization */}
                                                {(goals.meetCareCosts || goals.maximizeGovtSupport || goals.preserveInheritance) && (
                                                    <div className="pt-3 border-t border-gray-100">
                                                        <p className="text-sm font-medium text-gray-900 mb-2">Based on your goals:</p>
                                                        <div className="space-y-1.5 text-sm text-gray-700">
                                                            {goals.meetCareCosts && (
                                                                <div className="flex items-start gap-2">
                                                                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                    <span>Prioritize strategies that ensure sustainable cashflow and minimize contribution rates.</span>
                                                                </div>
                                                            )}
                                                            {goals.maximizeGovtSupport && (
                                                                <div className="flex items-start gap-2">
                                                                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                    <span>Prioritize strategies that increase Age Pension and reduce contribution rates.</span>
                                                                </div>
                                                            )}
                                                            {goals.preserveInheritance && (
                                                                <div className="flex items-start gap-2">
                                                                    <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                    <span>Prioritize strategies that maximize estate value while meeting care needs.</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {goalPriority && (
                                                            <div className="mt-3 pt-3 border-t border-gray-100">
                                                                <p className="text-xs font-medium text-gray-800">
                                                                    Primary Focus: {
                                                                        goalPriority === 'cashflow' && 'Maximizing cashflow to meet care costs'
                                                                    }
                                                                    {goalPriority === 'pension' && 'Maximizing Age Pension and minimizing contribution rates'}
                                                                    {goalPriority === 'estate' && 'Preserving inheritance while meeting care needs'}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* What You'll See */}
                                                <div className="pt-3 border-t border-gray-100">
                                                    <p className="text-sm font-medium text-gray-900 mb-1.5">What you'll see in the results:</p>
                                                    <p className="text-sm text-gray-700 leading-relaxed">
                                                        Each strategy will show the impact on <strong>Age Pension</strong>, <strong>Cash Flow</strong>, <strong>Contribution Rates</strong>, and <strong>Legacy Estimate</strong> to help you understand the full financial picture.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Main Question Section */}
                                <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-5">
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Lightbulb className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-blue-800 mb-2">Which strategies should we explore?</h3>
                                            <p className="text-blue-700 leading-relaxed">
                                                Select the types of financial strategies you'd like us to model. Click the arrow next to each strategy to learn more about what it is and how it might help.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {/* Gifting to Family */}
                                        <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                            strategySettings.includeGifting ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                        }`}>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={strategySettings.includeGifting}
                                                    onChange={(e) => setStrategySettings(prev => ({ ...prev, includeGifting: e.target.checked }))}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="font-semibold text-gray-800">Gifting to Family</div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleExplanation('gifting');
                                                            }}
                                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                                        >
                                                            {expandedExplanations.gifting ? (
                                                                <ChevronUp className="w-5 h-5" />
                                                            ) : (
                                                                <ChevronDown className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    {expandedExplanations.gifting && (
                                                        <div className="mt-3 space-y-2 pt-3 border-t border-gray-200">
                                                            <div className="text-gray-700 text-sm">
                                                                <strong>What it is:</strong> Giving money or assets to family members within Centrelink's gifting rules ($10,000 per year or $30,000 over 5 years).
                                                            </div>
                                                            <div className="text-gray-600 text-sm">
                                                                <strong>How it helps with Support at Home:</strong> By reducing your assessable assets, you may qualify for more Age Pension, which can help fund your care costs. However, you're giving up liquid assets that could be used directly for care expenses, so we'll model the trade-off between Age Pension benefit and lost income/capital.
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Funeral Bonds */}
                                        <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                            strategySettings.includeFuneralBonds ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                        }`}>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={strategySettings.includeFuneralBonds}
                                                    onChange={(e) => setStrategySettings(prev => ({ ...prev, includeFuneralBonds: e.target.checked }))}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="font-semibold text-gray-800">Funeral Bonds</div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleExplanation('funeralBonds');
                                                            }}
                                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                                        >
                                                            {expandedExplanations.funeralBonds ? (
                                                                <ChevronUp className="w-5 h-5" />
                                                            ) : (
                                                                <ChevronDown className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    {expandedExplanations.funeralBonds && (
                                                        <div className="mt-3 space-y-2 pt-3 border-t border-gray-200">
                                                            <div className="text-gray-700 text-sm">
                                                                <strong>What it is:</strong> A prepaid funeral investment product that is exempt from Centrelink&apos;s asset test (up to {formatCurrency(FUNERAL_BOND_LIMIT)} per person).
                                                            </div>
                                                            <div className="text-gray-600 text-sm">
                                                                <strong>How it helps with Support at Home:</strong> By converting assessable assets (like bank accounts) into an exempt asset, you may increase your Age Pension entitlement. This can help fund your care costs while also covering future funeral expenses. We'll model the impact on your Age Pension and contribution rates.
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Lifetime Annuities */}
                                        <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                            strategySettings.includeAnnuities ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                        }`}>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={strategySettings.includeAnnuities}
                                                    onChange={(e) => setStrategySettings(prev => ({ ...prev, includeAnnuities: e.target.checked }))}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="font-semibold text-gray-800">Lifetime Annuities</div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleExplanation('annuities');
                                                            }}
                                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                                        >
                                                            {expandedExplanations.annuities ? (
                                                                <ChevronUp className="w-5 h-5" />
                                                            ) : (
                                                                <ChevronDown className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    {expandedExplanations.annuities && (
                                                        <div className="mt-3 space-y-2 pt-3 border-t border-gray-200">
                                                            {/*
                                                              * THE 30% RULE, CORRECTED (2026-09-01). This said "30% if
                                                              * purchased with super", which is not the rule and not what
                                                              * the engine applies. The rate drops to 30% at the THRESHOLD
                                                              * DAY — the later of age 85 and five years after purchase —
                                                              * regardless of whether super or savings paid for it. As
                                                              * written it told a 70-year-old with super that only 30%
                                                              * would count from day one, when 60% will, for fifteen years.
                                                              */}
                                                            <div className="text-gray-700 text-sm">
                                                                <strong>What it is:</strong> An insurance product where you exchange a lump sum for guaranteed regular income payments for life. Centrelink counts only 60% of the purchase price as an asset for the first five years, or until age 85 if that is later, and 30% after that.
                                                            </div>
                                                            <div className="text-gray-600 text-sm">
                                                                <strong>How it helps with Support at Home:</strong> By reducing your assessable assets, you may qualify for more Age Pension. The annuity also provides guaranteed income that can help fund your care costs. However, your capital is locked in, so we'll model the trade-off between Age Pension benefit, guaranteed income, and reduced liquidity for ongoing expenses.
                                                            </div>
                                                            {/*
                                                              * The full explanation, one click away — an annuity is a
                                                              * foreign concept to most clients and this box sells the
                                                              * upside without naming what is given up (owner,
                                                              * 2026-09-01). Same modal every module uses.
                                                              */}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.preventDefault(); setShowAnnuityExplainer(true); }}
                                                                className="text-sm font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700"
                                                            >
                                                                What is an annuity, and what are the risks?
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <AnnuityExplainerModal open={showAnnuityExplainer} onClose={() => setShowAnnuityExplainer(false)} />

                                        {/* Debt repayment — shown when liabilities exist in profile */}
                                        {clientHasDebts && (
                                            <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                                strategySettings.includeDebtRepayment ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                            }`}>
                                                <div className="flex items-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={strategySettings.includeDebtRepayment !== false}
                                                        onChange={(e) => setStrategySettings(prev => ({
                                                            ...prev,
                                                            includeDebtRepayment: e.target.checked
                                                        }))}
                                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800">Debt Repayment</div>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            Model paying down {clientLiabilities.length} recorded liabilit{clientLiabilities.length === 1 ? 'y' : 'ies'} ({formatCurrency(clientLiabilities.reduce((s, l) => s + l.balance, 0))} total) using liquid assets. Downsize scenarios may also allocate sale proceeds to debt.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Property Strategies */}
                                        <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                            strategySettings.includePropertyStrategies ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                        }`}>
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={strategySettings.includePropertyStrategies}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setStrategySettings(prev => ({
                                                            ...prev,
                                                            includePropertyStrategies: checked,
                                                            // If unchecking main box, uncheck all sub-options
                                                            // If checking main box, check all sub-options
                                                            propertyStrategies: checked ? {
                                                                downsize: true,
                                                                downsizeNewHomeValue: prev.propertyStrategies?.downsizeNewHomeValue ?? 0,
                                                                downsizeSellingCosts: prev.propertyStrategies?.downsizeSellingCosts ?? 0,
                                                                increaseHomeEquity: true,
                                                                reverseMortgage: true,
                                                                heas: true
                                                            } : prev.propertyStrategies
                                                        }));
                                                    }}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="font-semibold text-gray-800">Property Strategies</div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleExplanation('propertyStrategies');
                                                            }}
                                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                                        >
                                                            {expandedExplanations.propertyStrategies ? (
                                                                <ChevronUp className="w-5 h-5" />
                                                            ) : (
                                                                <ChevronDown className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    {expandedExplanations.propertyStrategies && (
                                                        <div className="mt-3 space-y-3 pt-3 border-t border-gray-200">
                                                            <div className="text-gray-700 text-sm mb-3">
                                                                <strong>What it is:</strong> Strategies involving your home to free up capital or improve your financial position. Select which options you'd like us to explore:
                                                            </div>
                                                            {strategySettings.includePropertyStrategies && (
                                                                <div className="space-y-2 pl-4 border-l-2 border-blue-200">
                                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={strategySettings.propertyStrategies.downsize}
                                                                            onChange={(e) => setStrategySettings(prev => ({
                                                                                ...prev,
                                                                                propertyStrategies: {
                                                                                    ...prev.propertyStrategies,
                                                                                    downsize: e.target.checked
                                                                                }
                                                                            }))}
                                                                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-0.5"
                                                                        />
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-gray-800 text-sm">Downsizing</div>
                                                                            <div className="text-xs text-gray-600">Sell your current home and purchase a lower-cost property. Frees up capital for care costs while keeping the home exempt from asset testing.</div>
                                                                        </div>
                                                                    </label>
                                                                    {strategySettings.propertyStrategies.downsize && (
                                                                        <div className="ml-6 mt-1 p-3 bg-white rounded-lg border border-blue-100 space-y-3">
                                                                            <CurrencyInput
                                                                                label="New home value (if downsizing)"
                                                                                value={strategySettings.propertyStrategies.downsizeNewHomeValue || ''}
                                                                                onChange={(v) => setStrategySettings((prev) => ({
                                                                                    ...prev,
                                                                                    propertyStrategies: {
                                                                                        ...prev.propertyStrategies,
                                                                                        downsizeNewHomeValue: v ?? 0,
                                                                                    },
                                                                                }))}
                                                                                placeholder="Leave blank to explore a range"
                                                                            />
                                                                            <p className="text-xs text-gray-500 -mt-1">
                                                                                Current home: {formatCurrency(parseFloat(String(factFind?.home?.value)) || 0)}.
                                                                                {' '}Leave blank to model purchase prices at 50%, 60%, 70%, and 80% of current value.
                                                                            </p>
                                                                            <CurrencyInput
                                                                                label="Estimated selling costs ($)"
                                                                                value={strategySettings.propertyStrategies.downsizeSellingCosts || ''}
                                                                                onChange={(v) => setStrategySettings((prev) => ({
                                                                                    ...prev,
                                                                                    propertyStrategies: {
                                                                                        ...prev.propertyStrategies,
                                                                                        downsizeSellingCosts: v ?? 0,
                                                                                    },
                                                                                }))}
                                                                                placeholder="Leave blank for 2.5% default"
                                                                            />
                                                                            <p className="text-xs text-gray-500 -mt-1">
                                                                                Net proceeds = sale proceeds after costs minus new home purchase (same approach as Aged Care Strategy Analysis Setup).
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={
                                                                                strategySettings.propertyStrategies.increaseHomeEquity !== undefined
                                                                                    ? strategySettings.propertyStrategies.increaseHomeEquity
                                                                                    : Boolean(
                                                                                        strategySettings.propertyStrategies.renovate
                                                                                        || strategySettings.propertyStrategies.upgrade
                                                                                    )
                                                                            }
                                                                            onChange={(e) => setStrategySettings(prev => ({
                                                                                ...prev,
                                                                                propertyStrategies: {
                                                                                    ...prev.propertyStrategies,
                                                                                    increaseHomeEquity: e.target.checked,
                                                                                    renovate: undefined,
                                                                                    upgrade: undefined,
                                                                                }
                                                                            }))}
                                                                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-0.5"
                                                                        />
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-gray-800 text-sm">Increase exempt home equity</div>
                                                                            <div className="text-xs text-gray-600">Use liquid assets to renovate in place or buy a more expensive home. Modelled as $1 spent → $1 added to exempt home value (illustrative, not a market valuation).</div>
                                                                        </div>
                                                                    </label>
                                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={strategySettings.propertyStrategies.reverseMortgage}
                                                                            onChange={(e) => setStrategySettings(prev => ({
                                                                                ...prev,
                                                                                propertyStrategies: {
                                                                                    ...prev.propertyStrategies,
                                                                                    reverseMortgage: e.target.checked
                                                                                }
                                                                            }))}
                                                                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-0.5"
                                                                        />
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-gray-800 text-sm">Reverse Mortgage</div>
                                                                            <div className="text-xs text-gray-600">Borrow against your home's equity while continuing to live there. Provides cashflow without selling, but interest accrues and reduces your estate over time.</div>
                                                                        </div>
                                                                    </label>
                                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={strategySettings.propertyStrategies.heas}
                                                                            onChange={(e) => setStrategySettings(prev => ({
                                                                                ...prev,
                                                                                propertyStrategies: {
                                                                                    ...prev.propertyStrategies,
                                                                                    heas: e.target.checked
                                                                                }
                                                                            }))}
                                                                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-0.5"
                                                                        />
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-gray-800 text-sm">Home Equity Access Scheme (HEAS)</div>
                                                                            <div className="text-xs text-gray-600">Government scheme allowing you to access home equity as regular payments. Reduces Age Pension dollar-for-dollar but provides guaranteed income at lower rates than commercial reverse mortgages.</div>
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            )}
                                                            {!strategySettings.includePropertyStrategies && (
                                                                <div className="text-gray-500 text-sm italic">
                                                                    Enable "Property Strategies" above to select specific options.
                                                                </div>
                                                            )}
                                                            <div className="text-gray-700 text-sm mt-3 pt-3 border-t border-gray-200">
                                                                <strong>How it helps with Support at Home:</strong> Each strategy has different trade-offs. Downsizing frees up capital for care costs. Increasing exempt home equity can improve Age Pension by shifting assessable assets into your home. Reverse mortgages and HEAS provide cashflow but reduce Age Pension or estate. We'll model your selected options to show which best meets your goals.
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* How freed-up money is invested — one of the run's INPUTS, so it
                                    belongs in the setup beside the others rather than as an interim
                                    screen after "Continue to Analysis". The standalone gate below
                                    stays for runs that never open this modal, and suppresses itself
                                    once an answer is recorded. */}
                                {sahRunWouldInvest && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                                        <h4 className="font-semibold text-gray-800 mb-1">Investing freed-up money</h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Some strategies free up money — downsizing the home, for example. How would that money be invested?
                                        </p>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sah-new-money-profile">Investment approach</label>
                                        <select
                                            id="sah-new-money-profile"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={(factFind?.goals?.newMoneyInvestmentProfile as string) || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!setFactFind) return;
                                                setFactFind((prev) => {
                                                    const goals = { ...(prev.goals || {}) } as Record<string, unknown>;
                                                    if (value) goals.newMoneyInvestmentProfile = value; else delete goals.newMoneyInvestmentProfile;
                                                    return { ...prev, goals } as DraftFactFind;
                                                });
                                            }}
                                        >
                                            <option value="">Not sure — assume Conservative (most cautious)</option>
                                            {INVESTMENT_PROFILE_IDS.map((id) => (
                                                <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Saved to the client profile, so you&apos;ll only be asked once. Left unanswered, strategies that invest
                                            are priced conservatively and the results say so.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (hasStructures && setFactFind) {
                                        setFactFind((prev) => ({
                                            ...prev,
                                            structureFundingInSuggestions,
                                        }));
                                    }
                                    setShowSettingsModal(false);
                                    maybeRunAnalysis(true);
                                }}
                                disabled={isLinkedContextUnavailable}
                                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white rounded-lg hover:from-blue-600 hover:to-teal-500 transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
                            >
                                Continue to Analysis
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showNewMoneyGate && (
                <NewMoneyProfileGate
                    onAnswer={answerNewMoneyProfile}
                    onSkip={skipNewMoneyProfile}
                    contextLine="Some of the strategies I'll compare invest money that gets freed up — for example if downsizing your home is modelled."
                />
            )}
            {!showNewMoneyGate && needsAnnuityAnswer && (
                <AnnuityPreferenceGate
                    offerGuaranteed={guaranteedShapeAvailable(factFind as never)}
                    guaranteeWindow={guaranteeWindowWords({
                        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
                        sex: factFind?.client?.sex,
                        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
                        partnerSex: factFind?.partner?.sex,
                        ownerName: factFind?.client?.firstName,
                        partnerName: factFind?.partner?.firstName,
                    })}
                    onAnswer={answerAnnuityPreference}
                    onSkip={() => setAnnuityPrefSkipped(true)}
                    contextLine="Some of the strategies I'll compare move savings into a guaranteed income."
                />
            )}
            {showTbcGate && (
                <TransferCapGate
                    name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                    onAnswer={answerTbc}
                    onSkip={skipTbc}
                />
            )}

            {/* Error State */}
            {error && (
                <div className="bg-white border border-slate-200/70 border-l-4 border-red-400 p-4 rounded">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="text-red-600" />
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Problem Statement / Context */}
            {analysis && analysis.problemStatement && (
                <div className="bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                            <Target className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">Your Plan Snapshot</h3>
                            <p className="text-sm text-gray-600">A simple summary of your current position and the options we are comparing.</p>
                        </div>
                    </div>

                    {/* The service plan has changed since this analysis ran — say so, and offer the fix.
                        Without this, a user who edits services in the builder comes back to results
                        that silently model the OLD plan. */}
                    {analysis.servicePlanFingerprint !== undefined &&
                        analysis.servicePlanFingerprint !== JSON.stringify(effectiveServicePlan ?? {}) && (
                        <div className="mb-6 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-xl p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            <div className="text-sm text-slate-700 flex-1">
                                <p className="font-semibold mb-1">Your service plan has changed since this analysis was run</p>
                                <p className="mb-2">The results below still model the plan as it was. Re-run to compare strategies against the current services.</p>
                                <button
                                    type="button"
                                    onClick={() => runAnalysis()}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700"
                                >
                                    <RefreshCw className="w-4 h-4" /> Re-run with current plan
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Which services this analysis modelled — collapsed so it isn't a visual load. */}
                    <div className="mb-6">
                        <ServicePlanSummaryCollapsible
                            plan={(analysis.baseline?.servicePlanUsed ?? null) as SupportAtHomeServicePlan | null}
                            isEstimated={Boolean(analysis.baseline?.servicePlanIsEstimated)}
                            description={(analysis.baseline?.servicePlanDescription ?? null) as string | null}
                            isPartnered={factFind?.adviceFor === 'couple'}
                            clientName={factFind?.client?.firstName || 'Client'}
                            partnerName={factFind?.partner?.firstName || 'Partner'}
                        />
                    </div>

                    {/* Linked aged care scenario summary */}
                    {applyLinkedAgedCareScenario && (
                        <div className="mb-6 bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                            <div className="flex items-start gap-3 mb-3">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h4 className="text-base font-semibold text-gray-900">Linked Aged Care Scenario</h4>
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">Priority</span>
                                    </div>
                                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                                        We have applied your saved aged care scenario <strong className="text-gray-900">"{effectiveAgedCareIntegration?.name || 'Aged Care Strategy'}"</strong> first, then compared Support at Home options from that starting point.
                                    </p>
                                    <p className="text-xs text-gray-600 mb-3">
                                        For this analysis, contribution rates are based on <strong>{contributionContextMode === 'linked' ? 'your linked aged care scenario' : 'your current living arrangement'}</strong>.
                                    </p>
                                    <div className="flex items-center gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">RAD Paid:</span>
                                            <span className="ml-2 font-semibold text-gray-900">{formatCurrency(effectiveAgedCareIntegration?.radPaid || 0)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Annual Cost:</span>
                                            <span className="ml-2 font-semibold text-gray-900">{formatCurrency(effectiveAgedCareIntegration?.annualCost || 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Problem Statement Summary */}
                    <div className="mb-6">
                        <p className="text-sm text-gray-700 leading-relaxed">{analysis.problemStatement.summary}</p>
                    </div>
                    
                    {/* Current Position Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Age Pension Status</div>
                            <div className="text-lg font-semibold text-gray-900">
                                {analysis.problemStatement.pensionOptimization.status === 'full' ? 'Full Pension' :
                                 analysis.problemStatement.pensionOptimization.status === 'part' ? 'Part Pension' :
                                 'Self-Funded'}
                            </div>
                            {analysis.problemStatement.pensionOptimization.estimatedMaxGain > 0 && (
                                <div className="text-xs text-gray-500 mt-1">
                                    Potential gain: ${Math.round(analysis.problemStatement.pensionOptimization.estimatedMaxGain).toLocaleString()}/yr
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Annual Contribution</div>
                            <div className="text-lg font-semibold text-gray-900">
                                ${Math.round(analysis.problemStatement.contributionOptimization.annualContribution).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {analysis.problemStatement.contributionOptimization.ratesAtMinimum 
                                    ? 'Rates at minimum' 
                                    : `Optimisation: ${analysis.problemStatement.contributionOptimization.optimizationPotential}`}
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Year 1 Cashflow</div>
                            <div className={`text-lg font-semibold ${
                                analysis.problemStatement.cashflowPosition.year1Cashflow >= 0 
                                    ? 'text-green-600' 
                                    : 'text-red-600'
                            }`}>
                                ${Math.round(analysis.problemStatement.cashflowPosition.year1Cashflow).toLocaleString()}
                            </div>
                            {analysis.problemStatement.cashflowPosition.yearsToDeficit && (
                                <div className="text-xs text-amber-600 mt-1">
                                    Deficit in Year {analysis.problemStatement.cashflowPosition.yearsToDeficit}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Strategy Filtering Warnings - Consolidated */}
                    {analysis && analysis.strategyFiltering && analysis.strategyFiltering.warnings.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-start gap-3">
                                <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Strategy Considerations</h4>
                                    <ul className="space-y-2 text-sm text-gray-700">
                                        {analysis.strategyFiltering.warnings.map((warning, idx) => {
                                            // Check if message mentions NWOP Category B and add explanation
                                            const message = warning.message;
                                            return (
                                                <li key={idx} className="flex items-start gap-2">
                                                    <span className="text-gray-400 mt-0.5">•</span>
                                                    <span>
                                                        <strong className="text-gray-900">{warning.strategy}:</strong> {message.replace('NWOP Category B', 'NWOP Category B (see note below)')}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {analysis.strategyFiltering.warnings.some(w => w.message.includes('NWOP Category B')) && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <p className="text-xs text-gray-600 leading-relaxed">
                                                <strong className="text-gray-900">Note on NWOP Category B:</strong> If you were previously paying an Income Tested Care Fee (ITCF) of $1 or more per day before the new Support at Home system, you may qualify for NWOP Category B status. This provides lower contribution rates (0-25% for Independence and Everyday Living services) compared to standard rates, which can make certain strategies more beneficial for contribution optimisation.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Assumptions modal (Aged Care pattern) */}
            {showAssumptionsModal && analysis?.modellingAssumptions && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAssumptionsModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">Assumptions & limitations</h3>
                            <button type="button" onClick={() => setShowAssumptionsModal(false)} className="text-gray-500 hover:text-gray-700 p-1" aria-label="Close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 overflow-auto text-sm text-gray-700 space-y-4">
                            <p className="text-gray-600">
                                The Support at Home Suggestion Engine uses the following assumptions. For scenarios outside these, use Manual Control or build your Service Plan in Service Builder.
                            </p>
                            <ul className="list-disc list-inside space-y-2">
                                {assumptionsModalBullets.map((bullet, idx) => (
                                    <li key={idx}>
                                        {bullet.category && bullet.category !== 'Not factored in' ? (
                                            <span><strong>{bullet.category}:</strong> {bullet.text}</span>
                                        ) : (
                                            <span>{bullet.text}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Display */}
            {analysis && analysis.strategies && analysis.strategies.length > 0 && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-2xl font-bold text-gray-900">Recommended strategies</h3>
                        <div className="flex flex-wrap items-center gap-2">
                            {analysis.totalGenerated && (
                                <span className="text-sm text-gray-500">
                                    Evaluated {analysis.totalEvaluated} of {analysis.totalGenerated} strategies
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <p className="text-sm font-semibold text-slate-800 mb-3">Show strategies optimised for</p>
                        <div className="flex flex-wrap gap-2">
                            {([
                                { id: 'legacy', label: BEST_ESTATE_CHIP },
                                { id: 'covered', label: CASHFLOW_COVERED_CHIP },
                                { id: 'cashflow', label: 'Best cashflow' },
                                { id: 'pension', label: 'Best Age Pension' },
                            ] as const).map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setOptimisationFocus(id)}
                                    className={`flex-1 min-w-[140px] rounded-lg px-4 py-2.5 text-sm font-semibold transition-all border-2 ${
                                        optimisationFocus === id
                                            ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm'
                                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-slate-600 mt-3">
                            {optimisationFocus === 'covered' && 'The best estate among the plans that still pay the bills after the care contribution — or, where none does, whose shortfall the savings carry for the whole period. The card says which.'}
                            {optimisationFocus === 'legacy' && 'Estate focus — one primary strategy card. Other goal leaders are in Compare all strategies.'}
                            {optimisationFocus === 'cashflow' && 'Strongest modelled net annual cashflow (Year 1).'}
                            {optimisationFocus === 'pension' && 'Highest modelled Age Pension (if eligible).'}
                        </p>
                    </div>
                    
                    <div className="space-y-4">
                        {effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(recommendedOptimal) && (
                            <SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} />
                        )}
                        {displayedGoalStrategies.length === 0 && !(effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(recommendedOptimal)) && (
                            // No strategy beat "Do Nothing" on this goal, and the honest answer is to
                            // say so with the do-nothing figures — not to dress another goal's leader
                            // in this tab (which is what happened before: an "Optimal Cashflow" card
                            // under a banner claiming best estate).
                            <div className="rounded-lg border border-slate-300 border-dashed bg-slate-50 p-5 space-y-3">
                                <p className="font-semibold text-slate-900 leading-snug">
                                    {effectiveOptimisationSurface === 'covered' && 'No strategy could be ranked for this goal.'}
                                    {effectiveOptimisationSurface === 'legacy' && 'None of the modelled strategies improves your projected estate.'}
                                    {effectiveOptimisationSurface === 'cashflow' && 'None of the modelled strategies improves your net annual cashflow.'}
                                    {effectiveOptimisationSurface === 'pension' && 'None of the modelled strategies increases your Age Pension.'}
                                </p>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    {effectiveOptimisationSurface === 'pension'
                                        ? 'Keeping your current arrangement is the best modelled outcome for this goal — common when the Age Pension is already at its maximum for your situation.'
                                        : 'Each strategy modelled would cost more than it adds on this goal, so keeping your current arrangement is the best modelled outcome. Its figures are below.'}
                                    {' '}Use Compare all strategies to see what every option would do.
                                </p>
                                {analysis?.doNothingStrategy?.snapshot && (
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-white rounded-lg p-2 text-center border border-slate-200">
                                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Age Pension</p>
                                            <p className="text-sm font-semibold text-gray-900">{formatCurrency(analysis.doNothingStrategy.snapshot.pension ?? analysis.doNothingStrategy.snapshot.agePension ?? 0)}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-2 text-center border border-slate-200">
                                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Net Cashflow (Yr 1)</p>
                                            <p className="text-sm font-semibold text-gray-900">{formatCurrency(analysis.doNothingStrategy.snapshot.netCashflow ?? analysis.doNothingStrategy.snapshot.cashflow ?? 0)}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-2 text-center border border-slate-200">
                                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Projected Estate</p>
                                            <p className="text-sm font-semibold text-gray-900">{formatCurrency(analysis.doNothingStrategy.snapshot.legacyValue ?? 0)}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {displayedGoalStrategies.map((strategy, index) => {
                            const isCardHighlighted =
                                saSuggestionCardHighlightKey != null
                                    ? String(strategy.name) === saSuggestionCardHighlightKey
                                    : index === 0;
                            return (
                            <React.Fragment key={`${strategy.name}-${index}`}>
                            {index === 0 && effectiveOptimisationSurface === 'legacy' && (
                                <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                    <p className="font-semibold text-slate-900 leading-snug">Highest modelled projected estate for your selected goal.</p>
                                    <p className="text-slate-600 leading-relaxed">Use Compare all strategies below to see best cashflow and pension options side by side.</p>
                                </div>
                            )}
                            {index === 0 && effectiveOptimisationSurface === 'cashflow' && (
                                <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800">
                                    <p className="font-semibold text-slate-900">Strongest modelled net annual cashflow (Year 1).</p>
                                </div>
                            )}
                            {index === 0 && effectiveOptimisationSurface === 'pension' && (
                                <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800">
                                    <p className="font-semibold text-slate-900">Highest modelled Age Pension (if eligible).</p>
                                </div>
                            )}
                            <div 
                                key={strategy.name || index}
                                className={`bg-white border-2 rounded-lg p-6 shadow-lg ${isCardHighlighted ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'} hover:shadow-xl transition-shadow`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex flex-col gap-2 min-w-0">
                                        {(strategy.recommendationLabels || []).length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {strategy.recommendationLabels?.map((label) => (
                                                    <span
                                                        key={label}
                                                        className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium bg-blue-100 text-blue-800"
                                                    >
                                                        {friendlyGoalLabel(label)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {(() => {
                                            const cov = recommendedOptimal.find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === strategy.name)?.cashflowCovered;
                                            return cov ? <p className="text-[13px] text-slate-600">{cashflowCoveredNote(cov, currentSettings.legacyYears || legacyYears || 5, formatCurrency)}</p> : null;
                                        })()}
                                        <h3 className="text-xl font-bold text-gray-900">{strategy.name}</h3>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-bold text-blue-600">
                                            {formatCurrency(strategy.snapshot?.legacyValue || 0)}
                                        </div>
                                        <div className="text-sm text-gray-600">Projected Estate Value</div>
                                        <div className="text-xs">
                                            <JargonTerm term="how is this projected?">
                                                {'It starts from everything owned today and runs this plan forward year by year: pension and income in, care contributions and living costs out, investments growing at the assumed rates. The deep dive shows the year-by-year path behind this figure.'}
                                            </JargonTerm>
                                        </div>
                                    </div>
                                </div>

                                {/* The card's STORY — the same plain-words principle as every other module's
                                    cards. The what/how/consider narratives previously reached the reader only
                                    via the insight panel (which is being retired); they now live on the card,
                                    with the longer layers a tap away. */}
                                {(() => {
                                    const explainer = SAH_STRATEGY_EXPLAINERS[String(strategy.config?.type || '')];
                                    if (!explainer) {
                                        return strategy.config?.description ? (
                                            <p className="text-sm text-gray-600 mb-6">{strategy.config.description}</p>
                                        ) : null;
                                    }
                                    /*
                                     * Two additions the owner asked for (2026-08-25), both data-honest:
                                     * the rates sentence only renders when THIS strategy's rates actually
                                     * fall (quoting the same snapshot figures as the rates box below), and
                                     * the risk line surfaces the strategy's own vetted first
                                     * what-to-weigh-up point instead of boilerplate.
                                     */
                                    const ratesNow = strategy.snapshot?.contributionRates;
                                    const ratesBase = strategy.snapshot?.baselineContributionRates;
                                    const rateFell = (key: 'independence' | 'everydayLiving') =>
                                        !!ratesNow && !!ratesBase && (ratesNow[key] ?? 0) < (ratesBase[key] ?? 0) - 0.0005;
                                    const ratesImproved = rateFell('independence') || rateFell('everydayLiving');
                                    const asPct = (v: number | undefined) => `${((v ?? 0) * 100).toFixed(1)}%`;
                                    const keyRisk = explainer.consider[0];
                                    const moreToWeighUp = explainer.consider.slice(1).join(' ');
                                    return (
                                        <PlainWordsCard eyebrow="What this strategy does" className="mb-6">
                                            <p className="text-sm leading-relaxed text-slate-700">
                                                {explainer.what}
                                                {ratesImproved && ratesNow && ratesBase && (
                                                    <>
                                                        {' '}It also lowers the Support at Home contribution rates {'—'}{' '}
                                                        independence {asPct(ratesBase.independence)} {'→'}{' '}
                                                        <span className="font-semibold text-slate-900">{asPct(ratesNow.independence)}</span>,
                                                        everyday living {asPct(ratesBase.everydayLiving)} {'→'}{' '}
                                                        <span className="font-semibold text-slate-900">{asPct(ratesNow.everydayLiving)}</span>{' '}
                                                        {'—'} so every hour of service costs less (detailed below).
                                                    </>
                                                )}
                                            </p>
                                            {keyRisk && (
                                                <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                                                    <span className="font-semibold text-amber-700">Worth knowing:</span> {keyRisk}
                                                </p>
                                            )}
                                            <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                                                Tap for the detail:{' '}
                                                <JargonTerm term="how it works">{explainer.how.join(' ')}</JargonTerm>
                                                {moreToWeighUp && (
                                                    <>
                                                        {' · '}
                                                        <JargonTerm term="more to weigh up">{moreToWeighUp}</JargonTerm>
                                                    </>
                                                )}
                                            </p>
                                        </PlainWordsCard>
                                    );
                                })()}

                                {/* Asset Allocation - Show all assets modified/liquidated (matching Aged Care format) */}
                                {strategy.assetAllocation && strategy.assetAllocation.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-3">Asset Allocation</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {strategy.assetAllocation.map((asset, assetIndex) => (
                                                <div key={assetIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center">
                                                        <div className={`w-3 h-3 rounded-full mr-3 ${
                                                            asset.status === 'sold' ? 'bg-red-500' : 
                                                            asset.status === 'partiallySold' ? 'bg-yellow-500' : 
                                                            asset.status === 'new' ? 'bg-blue-500' :
                                                            asset.status === 'upgraded' ? 'bg-green-500' :
                                                            'bg-green-500'
                                                        }`}></div>
                                                        <span className="font-medium text-gray-700">
                                                            {asset.assetName}
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        {(asset.liquidatedAmount || 0) > 0 && (
                                                            <div className="text-sm text-red-600 mb-1">
                                                                Sold: -{formatCurrency(asset.liquidatedAmount)}
                                                            </div>
                                                        )}
                                                        <div className="font-semibold text-gray-900">
                                                            Kept: {formatCurrency(asset.remainingAmount)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Contribution Rate Changes */}
                                {strategy.snapshot?.contributionRates && strategy.snapshot?.baselineContributionRates && (
                                    <div className="mb-6 p-4 bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg">
                                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Contribution Rate Changes</h4>
                                        <div className="grid grid-cols-3 gap-4 text-xs">
                                            <div>
                                                <div className="text-gray-600 mb-1">Clinical</div>
                                                <div className="font-semibold">
                                                    {((strategy.snapshot.baselineContributionRates.clinical ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.clinical ?? 0) * 100).toFixed(1)}%
                                                </div>
                                                <div className={`text-xs ${(strategy.snapshot.contributionRates.clinical || 0) < (strategy.snapshot.baselineContributionRates.clinical || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                                    {((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) !== 0 ? 
                                                        `${((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.clinical || 0) - (strategy.snapshot.baselineContributionRates.clinical || 0)) * 100).toFixed(1)}%` : 
                                                        'No change'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-gray-600 mb-1">Independence</div>
                                                <div className="font-semibold">
                                                    {((strategy.snapshot.baselineContributionRates.independence ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.independence ?? 0) * 100).toFixed(1)}%
                                                </div>
                                                <div className={`text-xs ${(strategy.snapshot.contributionRates.independence || 0) < (strategy.snapshot.baselineContributionRates.independence || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                                    {((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) !== 0 ? 
                                                        `${((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.independence || 0) - (strategy.snapshot.baselineContributionRates.independence || 0)) * 100).toFixed(1)}%` : 
                                                        'No change'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-gray-600 mb-1">Everyday Living</div>
                                                <div className="font-semibold">
                                                    {((strategy.snapshot.baselineContributionRates.everydayLiving ?? 0) * 100).toFixed(1)}% → {((strategy.snapshot.contributionRates.everydayLiving ?? 0) * 100).toFixed(1)}%
                                                </div>
                                                <div className={`text-xs ${(strategy.snapshot.contributionRates.everydayLiving || 0) < (strategy.snapshot.baselineContributionRates.everydayLiving || 0) ? 'text-green-600' : 'text-gray-600'}`}>
                                                    {((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) !== 0 ? 
                                                        `${((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) > 0 ? '+' : ''}${(((strategy.snapshot.contributionRates.everydayLiving || 0) - (strategy.snapshot.baselineContributionRates.everydayLiving || 0)) * 100).toFixed(1)}%` : 
                                                        'No change'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Matching the Aged Care format, which is how it inherited the same mislabel:
                                these are full-year RATES, while the first projection year starts today and
                                is shorter. Calling them "Year 1" put two different numbers under one name —
                                this tile and the year-by-year table. Same wording as the aged care card and
                                the Financial Impact panel, so the product has one vocabulary for it. */}
                                <div className="mb-6">
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                                    <h4 className="text-lg font-semibold text-gray-800">Annual Snapshot</h4>
                                    <span className="text-xs text-gray-500">A normalised year</span>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">
                                    A full 12 months with this plan in place, so every strategy compares like for like.
                                    It is not the first financial year, which starts today and is shorter.
                                </p>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                                            <CalculationTooltip
                                                title="Age Pension (p.a.) Breakdown"
                                                data={strategy.snapshot?.agePensionBreakdown || []}
                                            >
                                                <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                                    Est. Age Pension
                                                    <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                                                </div>
                                            </CalculationTooltip>
                                            <div className="font-medium text-green-600">
                                                + {formatCurrency(strategy.snapshot?.pension || strategy.snapshot?.agePension || 0)}
                                            </div>
                                        </div>
                                        
                                        <div className="text-center p-3 bg-slate-50 rounded-lg">
                                            <div className="text-sm text-gray-600 mb-1">Investment Income</div>
                                            <div className="font-medium text-green-600">
                                                + {formatCurrency((strategy.snapshot?.abpIncome || 0) + (strategy.snapshot?.rentalIncome || 0) + (strategy.snapshot?.otherInvestmentIncome || 0))}
                                            </div>
                                        </div>
                                        
                                        <div className="text-center p-3 bg-slate-50 rounded-lg">
                                            <div className="text-sm text-gray-600 mb-1">Living Expenses</div>
                                            <div className="font-medium text-red-600">
                                                - {formatCurrency(strategy.snapshot?.livingExpenses || 0)}
                                            </div>
                                        </div>
                                        
                                        <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                                            <CalculationTooltip
                                                title="Support at Home contribution (your cost)"
                                                data={[
                                                    { label: 'Your annual contribution', value: formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0) },
                                                    { label: 'Gross service fees (catalogue)', value: formatCurrency(strategy.snapshot?.grossServicePlanCosts || strategy.snapshot?.supportAtHomeCostsBreakdown?.grossAnnual || 0) },
                                                    { label: '--- Gross by category ---', value: '' },
                                                    { label: 'Clinical', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.clinical || 0) },
                                                    { label: 'Independence', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.independence || 0) },
                                                    { label: 'Everyday Living', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.everydayLiving || 0) },
                                                    { label: '--- Your contribution ---', value: '' },
                                                    { label: 'Independence contribution', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.independenceContribution || 0) },
                                                    { label: 'Everyday living contribution', value: formatCurrency(strategy.snapshot?.supportAtHomeCostsBreakdown?.everydayContribution || 0) },
                                                    { label: 'Change vs baseline', value: formatCurrency(strategy.snapshot?.contributionReduction || 0) },
                                                ]}
                                            >
                                                <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                                    SAH contribution
                                                    <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                                                </div>
                                            </CalculationTooltip>
                                            <div className="font-medium text-red-600">
                                                - {formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0)}
                                            </div>
                                        </div>
                                        
                                        <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                                            <CalculationTooltip
                                                title="Tax Breakdown"
                                                data={strategy.snapshot?.taxBreakdown || [{ label: 'No tax payable.', value: '' }]}
                                            >
                                                <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                                    Income Tax
                                                    <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                                                </div>
                                            </CalculationTooltip>
                                            <div className={`font-medium ${(strategy.snapshot?.taxPayable ?? 0) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {(strategy.snapshot?.taxPayable ?? 0) < 0 ? '+' : '-'} {formatCurrency(Math.abs(strategy.snapshot?.taxPayable ?? 0))}
                                                {(strategy.snapshot?.taxPayable ?? 0) < 0 ? ' (refund)' : ''}
                                            </div>
                                        </div>
                                        
                                        <div className="text-center p-3 bg-slate-50 rounded-lg relative">
                                            <CalculationTooltip
                                                title="Net Annual Cashflow Breakdown"
                                                data={[
                                                    { label: 'Age Pension', value: formatCurrency(strategy.snapshot?.pension || strategy.snapshot?.agePension || 0) },
                                                    { label: 'Investment Income', value: formatCurrency((strategy.snapshot?.abpIncome || 0) + (strategy.snapshot?.rentalIncome || 0)) },
                                                    { label: '--- Less ---', value: '' },
                                                    { label: 'Living Expenses', value: formatCurrency(strategy.snapshot?.livingExpenses || 0) },
                                                    { label: 'SAH contribution (your cost)', value: formatCurrency(strategy.snapshot?.totalSupportAtHomeCosts || 0) },
                                                    { label: 'Income Tax', value: formatCurrency(Math.abs(strategy.snapshot?.taxPayable || 0)) },
                                                    { label: '--- Result ---', value: '' },
                                                    { label: 'Net Annual Cashflow', value: formatCurrency(strategy.snapshot?.cashflow || 0) }
                                                ]}
                                            >
                                                <div className="text-sm text-gray-600 mb-1 flex items-center justify-center">
                                                    Net Cashflow
                                                    <HelpCircle className="h-4 w-4 ml-1 text-gray-400 cursor-help" />
                                                </div>
                                            </CalculationTooltip>
                                            <div className={`font-medium ${(strategy.snapshot?.cashflow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(strategy.snapshot?.cashflow || 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Improvement Metrics */}
                                <div className="mb-6 p-4 bg-white rounded-lg border border-slate-200/70 border-l-4 border-blue-500">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Key Improvements vs Baseline</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="text-center">
                                            <div className="text-xs text-gray-600 mb-1">Pension Increase</div>
                                            <div className={`text-lg font-semibold ${(strategy.snapshot?.agePensionIncrease || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                                                {formatCurrency(strategy.snapshot?.agePensionIncrease || 0)}/yr
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-gray-600 mb-1">Contribution Reduction</div>
                                            <div className={`text-lg font-semibold ${(strategy.snapshot?.contributionReduction || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                                                {formatCurrency(strategy.snapshot?.contributionReduction || 0)}/yr
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-gray-600 mb-1">Cashflow Improvement</div>
                                            <div className={`text-lg font-semibold ${(strategy.snapshot?.cashflowImprovement || 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                                {formatCurrency(strategy.snapshot?.cashflowImprovement || 0)}/yr
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-gray-600 mb-1">Legacy Improvement</div>
                                            <div className={`text-lg font-semibold ${(strategy.snapshot?.legacyImprovement || 0) > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                                                {formatCurrency(strategy.snapshot?.legacyImprovement || 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/*
                                  * THE ANNUITY'S TERMS, where this strategy buys one (owner,
                                  * 2026-09-01). The card names the purchase amount and stops; the
                                  * owner, the residual, and whether the income survives the owner
                                  * were engine defaults with nothing on screen to say so.
                                  */}
                                <EngineAnnuityTerms
                                    className="mb-3"
                                    terms={(strategy as { annuityTerms?: Record<string, unknown> }).annuityTerms}
                                    factFind={factFind}
                                />

                                {/*
                                  * BORROWING AGAINST THE HOME, quantified.
                                  *
                                  * This module already named the product in the strategy title and
                                  * carried a what/how/consider explainer — better than Aged Care had.
                                  * What it lacked were the two things that actually caught the owner
                                  * out there (2026-09-01): the debt in DOLLARS, and the line saying
                                  * no repayment appears in the cashflow. There are no repayments, so
                                  * the loan puts nothing in the figures above, and its whole cost
                                  * lands in the closing balance.
                                  */}
                                {(() => {
                                    const er = (strategy as { equityRelease?: Record<string, unknown> }).equityRelease;
                                    if (!er || !(Number(er.loanAmount) > 0)) return null;
                                    return (
                                        <EquityReleaseTerms
                                            className="mb-3"
                                            kind={er.kind === 'heas' ? 'heas' : 'reverseMortgage'}
                                            loanAmount={Number(er.loanAmount) || 0}
                                            interestRate={Number(er.interestRate) || null}
                                            projectedBalance={Number(er.projectedBalance) || null}
                                            years={Number(er.years) || null}
                                            homeValue={Number(factFind?.home?.value) || null}
                                        />
                                    );
                                })()}

                                {/* Action Buttons */}
                                <div className="flex gap-2.5">
                                    <Button
                                        onClick={() => setRiskStrategy(strategy)}
                                        variant="secondary"
                                        size="sm"
                                        icon={ShieldAlert}
                                    >
                                        What could go wrong
                                    </Button>
                                    <Button
                                        onClick={() => openSaveStrategyModal(strategy)}
                                        variant="primary"
                                        size="sm"
                                        icon={Save}
                                    >
                                        Save Strategy
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (onStrategySelect) {
                                                onStrategySelect(strategy);
                                            }
                                            setSelectedStrategyForDeepDive(strategy);
                                            setDeepDiveModal(true);
                                        }}
                                        variant="secondary"
                                        size="sm"
                                        icon={Eye}
                                    >
                                        View Details
                                    </Button>
                                </div>
                            </div>
                            </React.Fragment>
                            );
                        })}
                    </div>

                    {analysis.strategies && analysis.strategies.length > 0 && (
                        <div className="mt-4 mx-auto flex w-full max-w-2xl justify-center px-2">
                            <div className="flex w-full gap-3">
                                {tableStrategies.length > 3 && (
                                    <Button
                                        type="button"
                                        onClick={() => setShowAllStrategiesTable(true)}
                                        variant="secondary"
                                        className="flex-1 basis-0"
                                    >
                                        Analyse more
                                    </Button>
                                )}
                                {goalComparisonRows.length > 0 && (
                                    <Button
                                        type="button"
                                        onClick={() => setShowGoalLeadersModal(true)}
                                        variant="primary"
                                        className="flex-1 basis-0"
                                    >
                                        Compare all strategies
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showAllStrategiesTable && tableStrategies.length > 0 && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAllStrategiesTable(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">All modelled strategies</h2>
                                    <p className="text-sm text-slate-600 mt-1.5">
                                        Every simulated combination — click a row to open Deep Dive. Sort by projected estate, cashflow, or pension.
                                        <span className="block mt-1 text-slate-500">{tableStrategies.length} simulated strategies</span>
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <label className="text-sm text-slate-600">Sort by</label>
                                    <select
                                        value={allStrategiesSortBy}
                                        onChange={(e) => setAllStrategiesSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')}
                                        className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                                    >
                                        <option value="legacy">Projected estate</option>
                                        <option value="cashflow">Net cashflow</option>
                                        <option value="pension">Age pension</option>
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setShowAllStrategiesTable(false)}
                                        className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>
                        <div className="flex-1 overflow-auto px-6 py-5 min-h-0">
                            <table className="w-full text-sm border-collapse rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-800">
                                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Projected estate</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Net cashflow</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableStrategies.map((s, idx) => (
                                        <tr
                                            key={`${s.name}-${idx}`}
                                            onClick={() => {
                                                setShowAllStrategiesTable(false);
                                                setSelectedStrategyForDeepDive(s);
                                                setDeepDiveModal(true);
                                            }}
                                            className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                        >
                                            <td className="px-4 py-3 font-medium text-slate-900 align-top">{s.name}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(s.snapshot?.legacyValue || 0)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(s.snapshot?.netCashflow ?? s.snapshot?.cashflow ?? 0)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(s.snapshot?.pension ?? s.snapshot?.agePension ?? 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showGoalLeadersModal && goalComparisonRows.length > 0 && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowGoalLeadersModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Compare all strategies</h2>
                                    <p className="text-sm text-slate-600 mt-1.5">
                                        Goal leaders only — best legacy, cashflow, and Age Pension (where eligible). Full list: <strong>Analyse more</strong>.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowGoalLeadersModal(false)}
                                    className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>
                        <div className="flex-1 overflow-auto px-6 py-5 min-h-0">
                            <table className="w-full text-sm border-collapse rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-800">
                                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Goal</th>
                                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Projected estate</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Net cashflow</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {goalComparisonRows.map(({ goalLabel, strategy }, idx) => (
                                        <tr
                                            key={`${goalLabel}-${strategy.name}-${idx}`}
                                            onClick={() => {
                                                setShowGoalLeadersModal(false);
                                                setSelectedStrategyForDeepDive(strategy);
                                                setDeepDiveModal(true);
                                            }}
                                            className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                        >
                                            <td className="px-4 py-3 font-medium text-slate-800 align-top">{goalLabel}</td>
                                            <td className="px-4 py-3 font-medium text-slate-900 align-top">{strategy.name}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(strategy.snapshot?.legacyValue || 0)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(strategy.snapshot?.netCashflow ?? strategy.snapshot?.cashflow ?? 0)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums align-top">{formatCurrency(strategy.snapshot?.pension ?? strategy.snapshot?.agePension ?? 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {analysis && (!analysis.strategies || analysis.strategies.length === 0) && !loading && (
                <div className="bg-white border border-slate-200/70 border-l-4 border-amber-400 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h4 className="font-semibold text-yellow-900 mb-2">No Strategies Found</h4>
                            <p className="text-slate-700 text-sm">
                                No viable strategies were found for your current situation. This could be because:
                            </p>
                            <ul className="list-disc list-inside text-slate-700 text-sm mt-2 space-y-1">
                                <li>Selected strategies are not relevant to your financial position</li>
                                <li>Insufficient liquid assets to implement strategies</li>
                                <li>You're already in an optimal position</li>
                            </ul>
                            <p className="text-slate-700 text-sm mt-3">
                                Try adjusting your strategy settings or review the plan snapshot above for guidance.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {riskStrategy && (
                <StrategyRisksPanel
                    strategyName={String(riskStrategy.name || '')}
                    onClose={() => setRiskStrategy(null)}
                    risks={buildStrategyRisks(riskKindsFromSupportAtHomeType(riskStrategy.config?.type, String(riskStrategy.name || '')))}
                />
            )}

            {deepDiveModal && selectedStrategyForDeepDive && (
                <SupportAtHomeSuggestionEngineDeepDive
                    strategy={selectedStrategyForDeepDive as unknown as SupportAtHomeSuggestionEngineDeepDiveProps['strategy']}
                    factFind={factFind}
                    onClose={() => setDeepDiveModal(false)}
                    estimatedServicePlanNote={
                        analysis?.baseline?.servicePlanCosts?.isEstimated
                            ? analysis.baseline.servicePlanCosts.estimatedDescription
                            : servicePlanCosts.isEstimated
                                ? servicePlanCosts.estimatedDescription
                                : null
                    }
                />
            )}

            {showSaveStrategyModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-5">
                            <h3 className="text-lg font-semibold">Save Strategy</h3>
                            <p className="text-blue-100 text-sm mt-1">
                                Save this suggestion-engine outcome for Service Builder and future comparison.
                            </p>
                        </div>
                        <div className="p-5 space-y-4">
                            {saveStrategySuccess ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-green-700 font-medium">
                                        <CheckCircle className="w-5 h-5" />
                                        Strategy saved successfully
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Generate the Statement of Advice for this strategy? It covers your position,
                                        the service plan modelled, this strategy&apos;s full rationale (benefits, risks,
                                        suitability), the alternatives considered and the year-by-year projections.
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => { void openDigitalSoaForSavedStrategy(); }}
                                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-semibold text-sm"
                                        >
                                            Open Digital SOA
                                        </button>
                                        <button
                                            type="button"
                                            onClick={closeSaveStrategyModal}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                                        >
                                            Maybe later
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label htmlFor="se-save-strategy-name" className="block text-sm font-medium text-gray-700 mb-1">
                                            Strategy name
                                        </label>
                                        <input
                                            id="se-save-strategy-name"
                                            type="text"
                                            value={saveStrategyName}
                                            onChange={(e) => setSaveStrategyName(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter a name for this strategy"
                                        />
                                        {saveStrategyNameError ? (
                                            <p className="text-sm text-red-600 mt-1" role="alert">{saveStrategyNameError}</p>
                                        ) : null}
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowSaveStrategyModal(false);
                                                setStrategyPendingSave(null);
                                            }}
                                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleConfirmSaveStrategy}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Initial State - Start Analysis Button */}
            {!loading && !analysis && (
                <div className="text-center py-12">
                    <BrainCircuit className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Model</h3>
                    {servicePlanCosts.isEstimated && (
                        <div className="mb-4 p-4 bg-white border border-slate-200/70 border-l-4 border-amber-400 rounded-lg max-w-2xl mx-auto text-left">
                            <p className="text-sm text-amber-800">
                                <strong>Note:</strong> You haven't built a service plan yet. Analysis will use a documented illustrative service mix so Support at Home costs are included in cashflow and legacy comparisons.
                                {servicePlanCosts.estimatedDescription && (
                                    <span className="block mt-2">{servicePlanCosts.estimatedDescription}</span>
                                )}
                            </p>
                        </div>
                    )}
                    <p className="text-gray-600 mb-6">
                        Click the settings icon to configure which strategies to explore, then start the intelligent modelling.
                    </p>
                    <button
                        onClick={() => setShowSettingsModal(true)}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-semibold flex items-center gap-2 mx-auto"
                    >
                        <Settings className="h-5 w-5" />
                        Configure Analysis
                    </button>
                </div>
            )}
        </div>
    );
};

export default SupportAtHomeSuggestionEnginePanel;

